import { openOAuthConnectPopup } from '@/pages/integrations/providers/oauth-connect-lifecycle'
import {
  Fragment,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
  type ReactNode,
} from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useParams } from '@tanstack/react-router'
import {
  AlertCircle,
  Check,
  ChevronDown,
  Copy,
  ExternalLink,
  Info,
  Loader2,
  Shield,
} from 'lucide-react'
import { toast } from 'sonner'
import type {
  CloudConnectionDTO,
  CloudConnectionAccountDTO,
  CloudProvider,
  CloudResourceUsage,
  CloudResourceUsageMetadata,
  ProviderServiceDTO,
} from '@deepguide-ai/types'
import { trpc, trpcClient } from '@/lib/trpc'
import { cn } from '@/lib/utils'
import { IntegrationIcon } from '@/pages/integrations/providers/icons'
import { Button } from '@deepguide-ai/ui/button'
import { Checkbox } from '@deepguide-ai/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@deepguide-ai/ui/dialog'
import { Textarea } from '@deepguide-ai/ui/textarea'
import { Input } from '@deepguide-ai/ui/input'
import { Label } from '@deepguide-ai/ui/label'
import { MultiSelect } from '@deepguide-ai/ui/multi-select'
import { Tabs, TabsList, TabsTrigger } from '@deepguide-ai/ui/tabs'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@deepguide-ai/ui/table'
import { TableEmptyState } from '@deepguide-ai/ui/table-empty-state'
import { ConfirmDialog } from '@/components/confirm-dialog'
import { useCloudResourceUsageDetection } from './useCloudResourceUsageDetection'

export type { CloudProvider } from '@deepguide-ai/types'

export type CloudAccessLevel = 'read_only' | 'read_write'

export type CloudConnectionInput =
  | { provider: 'aliyun' | 'tencent'; connectionId?: string; roleArn: string; regions: string[]; accessLevel?: 'read_only' }
  | {
      provider: 'aws'
      connectionId?: string
      authMode?: 'credentials' | 'assume_role'
      accessKeyId?: string
      secretAccessKey?: string
      sessionToken?: string
      roleArn?: string
      externalId?: string
      region?: string
      regions?: string[]
      accessLevel?: CloudAccessLevel
    }
  | {
      provider: 'cloudflare'
      connectionId?: string
      apiToken: string
      accountId: string
      accessLevel?: 'read_only'
    }

export type CloudProviderDefinition = {
  id: CloudProvider
  name: string
  description: string
  icon: FC<{ className?: string }>
}

export type CloudResourceService = ProviderServiceDTO

/** Account-level identity and audit logging: always in scope, never unticked. */
export const isEssentialService = (
  service: Pick<ProviderServiceDTO, 'relevance'>
) => service.relevance === 'essential'

/** Service sections shared by onboarding and Provider Detail → Services. */
export function groupCloudServices<T extends ProviderServiceDTO>(services: T[]) {
  return [
    {
      id: 'essential',
      title: 'Always checked',
      description:
        'These checks stay in scope. This does not mean the services are enabled or the checks have passed.',
      services: services.filter(isEssentialService),
    },
    {
      id: 'recommended',
      title: 'Recommended',
      description: 'Baseline coverage selected by default. You can turn it off.',
      services: services.filter(
        (service) => !isEssentialService(service) && service.recommendedByDefault
      ),
    },
    {
      id: 'other',
      title: 'Other services',
      description: 'Include these when they are used by this account.',
      services: services.filter(
        (service) => !isEssentialService(service) && !service.recommendedByDefault
      ),
    },
  ]
}

export function createCloudResourceUsageDraft(
  services: CloudResourceService[],
  resourceUsage: CloudResourceUsage | undefined
): CloudResourceUsage {
  return Object.fromEntries(
    services.map((service) => [
      service.id,
      isEssentialService(service)
        ? 'USED'
        : (resourceUsage?.[service.id] ??
          (resourceUsage?.sql === 'NOT_USED' &&
          ['sqlserver', 'mysql', 'postgresql'].includes(service.id)
            ? 'NOT_USED'
            : undefined) ??
          (service.recommendedByDefault ? 'USED' : 'UNKNOWN')),
    ])
  )
}

export function CloudResourceUsageFields({
  services,
  value,
  savedValue = value,
  metadata = {},
  disabled = false,
  savingIds = [],
  saveImmediately = false,
  showTestCount = true,
  showStatusTabs = true,
  search = '',
  renderServiceName,
  onViewChecks,
  onChange,
}: {
  services: CloudResourceService[]
  value: CloudResourceUsage
  savedValue?: CloudResourceUsage
  metadata?: CloudResourceUsageMetadata
  disabled?: boolean
  savingIds?: string[]
  saveImmediately?: boolean
  showTestCount?: boolean
  showStatusTabs?: boolean
  search?: string
  renderServiceName?: (service: CloudResourceService) => ReactNode
  onViewChecks?: () => void
  onChange: (value: CloudResourceUsage) => void
}) {
  const configurable = services.filter(
    (service) => !isEssentialService(service)
  )
  const groups = [
    { state: 'USED', label: 'In use' },
    { state: 'NOT_USED', label: 'Not used' },
    { state: 'UNKNOWN', label: 'Needs confirmation' },
  ] as const
  const [activeState, setActiveState] = useState<string | null>(null)
  const initialState = configurable.some(
    (service) => (savedValue[service.id] ?? 'UNKNOWN') === 'UNKNOWN'
  )
    ? 'UNKNOWN'
    : 'USED'
  const tab = activeState ?? initialState
  useEffect(() => {
    if (activeState === null && configurable.length > 0)
      setActiveState(initialState)
  }, [activeState, configurable.length, initialState])
  const [bulkIds, setBulkIds] = useState<string[]>([])
  const normalizedSearch = search.trim().toLocaleLowerCase()
  const visible = configurable.filter(
    (service) =>
      (!showStatusTabs || (savedValue[service.id] ?? 'UNKNOWN') === tab) &&
      (!normalizedSearch ||
        [service.label, service.description, service.id].some((text) =>
          text.toLocaleLowerCase().includes(normalizedSearch)
        ))
  )
  const unresolved = visible.filter(
    (service) => (value[service.id] ?? 'UNKNOWN') === 'UNKNOWN'
  )
  return (
    <div className='space-y-3'>
      {showStatusTabs && configurable.length > 0 && (
        <Tabs value={tab} onValueChange={setActiveState}>
          <TabsList className='w-full'>
            {groups.map((group) => (
              <TabsTrigger key={group.state} value={group.state}>
                {group.label}{' '}
                <span className='ml-1 tabular-nums'>
                  {
                    configurable.filter(
                      (service) =>
                        (savedValue[service.id] ?? 'UNKNOWN') === group.state
                    ).length
                  }
                </span>
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      )}
      {showStatusTabs && tab === 'UNKNOWN' && configurable.length > 0 && (
        <div className='flex flex-wrap items-center justify-between gap-2'>
          <p className='text-xs text-muted-foreground'>
            Confirm usage to determine whether related tests are needed.
          </p>
          {unresolved.length > 1 && (
            <Button
              type='button'
              variant='ghost'
              size='sm'
              disabled={disabled}
              onClick={() =>
                setBulkIds(unresolved.map((service) => service.id))
              }
            >
              Mark all shown as not used
            </Button>
          )}
        </div>
      )}
      {configurable.length > 0 && (
        <div className='overflow-hidden rounded-md border'>
          <Table className={cn('table-fixed', showTestCount ? 'min-w-xl' : 'min-w-md')}>
            <TableHeader>
              <TableRow>
                <TableHead>Service</TableHead>
                {showTestCount && (
                  <TableHead className='w-32'>Related tests</TableHead>
                )}
                <TableHead className='w-56'>Usage</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visible.map((service) => {
                const state = value[service.id] ?? 'UNKNOWN'
                const savedState = savedValue[service.id] ?? 'UNKNOWN'
                const changed = state !== savedState
                const source = metadata[service.id]?.source
                const sourceLabel =
                  source === 'USER'
                    ? 'Set manually'
                    : source === 'SCAN'
                      ? 'Detected automatically'
                      : service.recommendedByDefault && savedState === 'USED'
                        ? 'Recommended default'
                        : undefined
                return (
                  <TableRow key={service.id}>
                    <TableCell>
                      <div
                        className='text-sm font-medium'
                        title={changed ? 'Unsaved changes' : sourceLabel}
                      >
                        {renderServiceName?.(service) ?? service.label}
                      </div>
                      <div
                        className='max-w-lg truncate text-xs text-muted-foreground'
                        title={service.description}
                      >
                        {service.description}
                      </div>
                    </TableCell>
                    {showTestCount && (
                      <TableCell className='tabular-nums'>
                        {service.testCount}
                      </TableCell>
                    )}
                    <TableCell>
                      {savingIds.includes(service.id) ? (
                        <span role='status' className='inline-flex h-8 items-center gap-2 text-xs text-muted-foreground'>
                          <Loader2 className='size-3.5 animate-spin' />Saving…
                        </span>
                      ) : savedState === 'UNKNOWN' ? (
                        state === 'UNKNOWN' ? (
                          <div className='flex flex-wrap gap-2'>
                            <Button
                              type='button'
                              size='sm'
                              variant='outline'
                              disabled={disabled}
                              aria-label={`Use ${service.label}`}
                              onClick={() =>
                                onChange({ ...value, [service.id]: 'USED' })
                              }
                            >
                              In use
                            </Button>
                            <Button
                              type='button'
                              size='sm'
                              variant='outline'
                              disabled={disabled}
                              aria-label={`Do not use ${service.label}`}
                              onClick={() =>
                                onChange({ ...value, [service.id]: 'NOT_USED' })
                              }
                            >
                              Not used
                            </Button>
                          </div>
                        ) : (
                          <div className='flex flex-wrap items-center gap-2'>
                            <span className='text-xs text-muted-foreground'>
                              Will be marked{' '}
                              {state === 'USED' ? 'in use' : 'not used'}
                            </span>
                            <Button
                              type='button'
                              size='sm'
                              variant='ghost'
                              disabled={disabled}
                              aria-label={`Change ${service.label} decision`}
                              onClick={() =>
                                onChange({ ...value, [service.id]: 'UNKNOWN' })
                              }
                            >
                              Change
                            </Button>
                          </div>
                        )
                      ) : (
                        <div className='space-y-1'>
                          <label className='flex items-center gap-2 whitespace-nowrap text-sm'>
                            <Checkbox
                              aria-label={`${service.label} usage`}
                              disabled={disabled}
                              checked={state === 'USED'}
                              onCheckedChange={(checked) =>
                                onChange({
                                  ...value,
                                  [service.id]:
                                    checked === true ? 'USED' : 'NOT_USED',
                                })
                              }
                            />
                            {state === 'USED' ? 'In use' : 'Not used'}
                          </label>
                          {changed && (
                            <span className='text-xs text-muted-foreground'>
                              Unsaved changes
                            </span>
                          )}
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                )
              })}
              {visible.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={showTestCount ? 3 : 2}
                    className='p-0'
                  >
                    <TableEmptyState
                      className='min-h-40 rounded-none border-0'
                      message={normalizedSearch
                        ? `No services match “${search.trim()}” in this group.`
                        : 'No services in this group.'}
                    />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}
      {services.some(isEssentialService) && (
        <div className='flex flex-wrap items-baseline gap-x-2 gap-y-1 text-xs text-muted-foreground'>
          <p>Account-level identity and audit logging checks are always included.</p>
          {onViewChecks && (
            <Button
              type='button'
              variant='link'
              size='sm'
              className='h-auto p-0 text-xs'
              onClick={onViewChecks}
            >
              View checks
            </Button>
          )}
        </div>
      )}
      <ConfirmDialog
        open={bulkIds.length > 0}
        onOpenChange={(open) => {
          if (!open) setBulkIds([])
        }}
        title={`Mark ${bulkIds.length} services as not used?`}
        desc={`${services
          .filter((service) => bulkIds.includes(service.id))
          .map((service) => service.label)
          .join(
            ', '
          )}. ${saveImmediately ? 'Confirm to save these changes immediately.' : 'This updates your draft only; save changes to apply it.'}`}
        confirmText='Mark as not used'
        disabled={disabled}
        handleConfirm={() => {
          onChange({
            ...value,
            ...Object.fromEntries(
              bulkIds
                .filter((id) => (value[id] ?? 'UNKNOWN') === 'UNKNOWN')
                .map((id) => [id, 'NOT_USED' as const])
            ),
          })
          setBulkIds([])
        }}
      />
    </div>
  )
}

const AWSLogo: FC<{ className?: string }> = ({ className }) => (
  <svg
    className={cn(className, 'dark:[&>path:first-child]:fill-white')}
    viewBox='0 0 24 24'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path
      d='M6.763 10.036C6.763 10.358 6.795 10.618 6.852 10.806C6.917 10.994 6.998 11.198 7.111 11.418C7.159 11.498 7.175 11.578 7.175 11.65C7.175 11.754 7.111 11.858 6.974 11.962L6.499 12.277C6.402 12.341 6.306 12.373 6.218 12.373C6.106 12.373 5.994 12.317 5.882 12.213C5.769 12.101 5.673 11.973 5.593 11.829C5.513 11.677 5.433 11.509 5.345 11.309C4.757 12.013 4.008 12.365 3.098 12.365C2.444 12.365 1.926 12.181 1.554 11.813C1.182 11.445 0.992 10.958 0.992 10.35C0.992 9.702 1.214 9.175 1.666 8.775C2.118 8.375 2.716 8.175 3.468 8.175C3.728 8.175 3.996 8.199 4.28 8.239C4.564 8.279 4.856 8.343 5.164 8.415V7.903C5.164 7.327 5.044 6.919 4.812 6.671C4.572 6.423 4.168 6.303 3.588 6.303C3.316 6.303 3.036 6.335 2.748 6.407C2.46 6.479 2.18 6.567 1.908 6.679C1.796 6.727 1.708 6.759 1.652 6.775C1.596 6.791 1.556 6.799 1.524 6.799C1.396 6.799 1.332 6.711 1.332 6.527V6.167C1.332 6.023 1.356 5.919 1.412 5.855C1.468 5.791 1.564 5.727 1.708 5.663C1.98 5.527 2.3 5.415 2.668 5.327C3.036 5.231 3.428 5.187 3.844 5.187C4.732 5.187 5.385 5.391 5.813 5.799C6.233 6.207 6.449 6.831 6.449 7.671V10.036H6.763ZM3.428 11.309C3.68 11.309 3.94 11.261 4.212 11.165C4.484 11.069 4.724 10.894 4.924 10.654C5.044 10.51 5.132 10.35 5.172 10.166C5.212 9.982 5.244 9.766 5.244 9.518V9.23C5.02 9.174 4.78 9.126 4.532 9.094C4.284 9.062 4.044 9.046 3.812 9.046C3.316 9.046 2.948 9.142 2.692 9.342C2.436 9.542 2.312 9.822 2.312 10.19C2.312 10.534 2.404 10.79 2.596 10.966C2.78 11.197 3.06 11.309 3.428 11.309ZM9.429 12.277C9.269 12.277 9.165 12.253 9.101 12.197C9.037 12.149 8.981 12.037 8.933 11.877L7.243 5.919C7.195 5.751 7.171 5.639 7.171 5.575C7.171 5.439 7.243 5.367 7.387 5.367H8.083C8.251 5.367 8.363 5.391 8.419 5.447C8.483 5.495 8.531 5.607 8.579 5.767L9.789 10.654L10.903 5.767C10.943 5.599 10.991 5.495 11.055 5.447C11.119 5.399 11.239 5.367 11.399 5.367H11.967C12.135 5.367 12.247 5.391 12.311 5.447C12.375 5.495 12.431 5.607 12.463 5.767L13.593 10.718L14.835 5.767C14.883 5.599 14.939 5.495 14.995 5.447C15.059 5.399 15.171 5.367 15.331 5.367H15.995C16.139 5.367 16.219 5.431 16.219 5.575C16.219 5.615 16.211 5.655 16.203 5.703C16.195 5.751 16.179 5.815 16.147 5.903L14.409 11.861C14.361 12.029 14.305 12.133 14.241 12.181C14.177 12.229 14.065 12.261 13.913 12.261H13.305C13.137 12.261 13.025 12.237 12.961 12.181C12.897 12.125 12.841 12.021 12.809 11.853L11.703 7.095L10.605 11.845C10.565 12.013 10.509 12.117 10.445 12.173C10.381 12.229 10.261 12.261 10.101 12.261H9.429V12.277ZM19.268 12.405C18.844 12.405 18.42 12.357 18.012 12.261C17.604 12.165 17.284 12.061 17.068 11.941C16.932 11.861 16.844 11.773 16.812 11.693C16.78 11.613 16.764 11.525 16.764 11.445V11.069C16.764 10.885 16.836 10.797 16.972 10.797C17.02 10.797 17.068 10.805 17.116 10.821C17.164 10.837 17.236 10.869 17.316 10.901C17.58 11.021 17.868 11.117 18.172 11.181C18.484 11.245 18.788 11.277 19.1 11.277C19.596 11.277 19.98 11.189 20.244 11.013C20.508 10.837 20.644 10.582 20.644 10.254C20.644 10.03 20.572 9.846 20.428 9.694C20.284 9.542 20.012 9.406 19.62 9.278L18.5 8.934C17.908 8.75 17.468 8.478 17.196 8.118C16.924 7.766 16.784 7.367 16.784 6.935C16.784 6.583 16.86 6.271 17.012 5.999C17.164 5.727 17.372 5.495 17.628 5.303C17.884 5.103 18.18 4.959 18.524 4.855C18.868 4.751 19.228 4.703 19.612 4.703C19.804 4.703 20.004 4.711 20.196 4.735C20.396 4.759 20.58 4.791 20.764 4.831C20.94 4.879 21.108 4.927 21.268 4.983C21.428 5.039 21.548 5.095 21.636 5.151C21.756 5.223 21.844 5.295 21.892 5.375C21.94 5.447 21.964 5.551 21.964 5.687V6.031C21.964 6.215 21.892 6.311 21.756 6.311C21.692 6.311 21.588 6.279 21.452 6.215C21.012 6.015 20.524 5.919 19.988 5.919C19.54 5.919 19.188 5.991 18.948 6.143C18.708 6.295 18.584 6.527 18.584 6.847C18.584 7.071 18.664 7.263 18.824 7.415C18.984 7.567 19.276 7.719 19.692 7.855L20.788 8.199C21.372 8.383 21.796 8.639 22.052 8.967C22.308 9.295 22.432 9.678 22.432 10.11C22.432 10.47 22.356 10.798 22.212 11.085C22.06 11.373 21.852 11.621 21.588 11.821C21.324 12.029 21.004 12.181 20.636 12.285C20.252 12.365 19.812 12.405 19.268 12.405Z'
      fill='#252F3E'
    />
    <path
      d='M20.516 16.413C18.38 17.971 15.316 18.791 12.684 18.791C8.988 18.791 5.66 17.459 3.148 15.211C2.948 15.027 3.132 14.779 3.372 14.923C6.068 16.481 9.38 17.427 12.804 17.427C15.116 17.427 17.652 16.955 19.98 15.979C20.324 15.827 20.62 16.197 20.516 16.413Z'
      fill='#FF9900'
    />
    <path
      d='M21.292 15.523C21.028 15.179 19.524 15.363 18.844 15.443C18.636 15.467 18.604 15.283 18.788 15.155C19.988 14.323 21.924 14.563 22.148 14.843C22.372 15.131 22.084 17.075 20.956 18.011C20.788 18.155 20.628 18.075 20.7 17.891C20.924 17.323 21.556 15.859 21.292 15.523Z'
      fill='#FF9900'
    />
  </svg>
)

export const SCREENATA_CLOUD_PROVIDERS: CloudProviderDefinition[] = [
  {
    id: 'aws',
    name: 'AWS',
    description:
      'Scan IAM roles, S3 bucket policies, CloudTrail logs, and security groups.',
    icon: AWSLogo,
  },
  {
    id: 'gcp',
    name: 'Google Cloud',
    description: 'Scan IAM, Cloud Storage, audit logs, and firewall posture.',
    icon: ({ className }) => (
      <span className={className}>
        <IntegrationIcon type='GCP_READONLY' />
      </span>
    ),
  },
  {
    id: 'azure',
    name: 'Azure',
    description:
      'Scan role assignments, activity logs, and network security groups.',
    icon: ({ className }) => (
      <span className={className}>
        <IntegrationIcon type='AZURE_READONLY' />
      </span>
    ),
  },
  {
    id: 'cloudflare',
    name: 'Cloudflare',
    description: 'Scan account access, zones, WAF, and R2 storage posture.',
    icon: ({ className }) => (
      <span className={className}>
        <IntegrationIcon type='Cloudflare' />
      </span>
    ),
  },
  { id: 'tencent', name: 'Tencent Cloud', description: 'Discover CAM access, CVM instances and CloudAudit configuration.',
    icon: ({ className }) => <span className={className}><IntegrationIcon type='TencentCloud' /></span> },
  { id: 'aliyun', name: 'Alibaba Cloud (Aliyun)', description: 'Discover RAM access, ECS instances and ActionTrail configuration.',
    icon: ({ className }) => <span className={className}><IntegrationIcon type='Aliyun' /></span> },
]

const SCREENATA_CLOUD_PROVIDER_MAP: Record<
  CloudProvider,
  CloudProviderDefinition
> = {
  aws: SCREENATA_CLOUD_PROVIDERS[0],
  gcp: SCREENATA_CLOUD_PROVIDERS[1],
  azure: SCREENATA_CLOUD_PROVIDERS[2],
  cloudflare: SCREENATA_CLOUD_PROVIDERS[3],
  tencent: SCREENATA_CLOUD_PROVIDERS[4],
  aliyun: SCREENATA_CLOUD_PROVIDERS[5],
}

type CloudConfigFormState = {
  delegatedRoleArn: string
  delegatedRegions: string
  awsAccessKeyId: string
  awsSecretAccessKey: string
  awsSessionToken: string
  awsRoleArn: string
  awsRegion: string
  cloudflareApiToken: string
  cloudflareAccountId: string
  accessLevel: CloudAccessLevel
}

type CloudOAuthProvider = Exclude<CloudProvider, 'aws' | 'cloudflare' | 'aliyun' | 'tencent'>

const EMPTY_CLOUD_FORM: CloudConfigFormState = {
  delegatedRoleArn: '',
  delegatedRegions: '',
  awsAccessKeyId: '',
  awsSecretAccessKey: '',
  awsSessionToken: '',
  awsRoleArn: '',
  awsRegion: '',
  cloudflareApiToken: '',
  cloudflareAccountId: '',
  accessLevel: 'read_only',
}

const AWS_ROLE_ARN_REGEX = /^arn:aws(?:-[a-z-]+)?:iam::\d{12}:role\/.+$/

function asString(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null
}

const AWS_REGION_PATTERN = /^[a-z]{2}-[a-z-]+-\d+$/

/** AWS commercial regions for the Scope-step multi-select. Values are the
 *  region codes; the on-submit parser still validates them against
 *  `AWS_REGION_PATTERN`, so an empty selection means "all enabled regions". */
const AWS_REGIONS: { label: string; value: string }[] = [
  { label: 'us-east-1 · N. Virginia', value: 'us-east-1' },
  { label: 'us-east-2 · Ohio', value: 'us-east-2' },
  { label: 'us-west-1 · N. California', value: 'us-west-1' },
  { label: 'us-west-2 · Oregon', value: 'us-west-2' },
  { label: 'ca-central-1 · Canada', value: 'ca-central-1' },
  { label: 'eu-west-1 · Ireland', value: 'eu-west-1' },
  { label: 'eu-west-2 · London', value: 'eu-west-2' },
  { label: 'eu-west-3 · Paris', value: 'eu-west-3' },
  { label: 'eu-central-1 · Frankfurt', value: 'eu-central-1' },
  { label: 'eu-north-1 · Stockholm', value: 'eu-north-1' },
  { label: 'eu-south-1 · Milan', value: 'eu-south-1' },
  { label: 'ap-south-1 · Mumbai', value: 'ap-south-1' },
  { label: 'ap-southeast-1 · Singapore', value: 'ap-southeast-1' },
  { label: 'ap-southeast-2 · Sydney', value: 'ap-southeast-2' },
  { label: 'ap-northeast-1 · Tokyo', value: 'ap-northeast-1' },
  { label: 'ap-northeast-2 · Seoul', value: 'ap-northeast-2' },
  { label: 'ap-northeast-3 · Osaka', value: 'ap-northeast-3' },
  { label: 'ap-east-1 · Hong Kong', value: 'ap-east-1' },
  { label: 'sa-east-1 · São Paulo', value: 'sa-east-1' },
  { label: 'me-south-1 · Bahrain', value: 'me-south-1' },
  { label: 'af-south-1 · Cape Town', value: 'af-south-1' },
]

function parseAwsRegions(
  value: string
): { ok: true; regions: string[] } | { ok: false; invalidRegions: string[] } {
  const regions = Array.from(
    new Set(
      value
        .split(/[\s,]+/)
        .map((region) => region.trim())
        .filter(Boolean)
    )
  )
  const invalidRegions = regions.filter(
    (region) => !AWS_REGION_PATTERN.test(region)
  )
  if (invalidRegions.length > 0) {
    return { ok: false, invalidRegions }
  }
  return { ok: true, regions }
}

function getAwsRegionScope(
  value: string
):
  | { ok: true; region: string; regions?: string[] }
  | { ok: false; invalidRegions: string[] } {
  const parsed = parseAwsRegions(value)
  if (!parsed.ok) return parsed
  const region = parsed.regions[0] ?? 'us-east-1'
  return {
    ok: true,
    region,
    ...(parsed.regions.length > 0 ? { regions: parsed.regions } : {}),
  }
}

/** Group AWS remediation action strings (e.g. "s3:PutBucketEncryption") by
 *  their service prefix, for a compact human-scannable permission list on the
 *  read+write step. */
function groupAwsRemediationActions(
  actions: string[]
): { service: string; count: number }[] {
  const byService = new Map<string, number>()
  for (const action of actions) {
    const service = action.split(':')[0] || action
    byService.set(service, (byService.get(service) ?? 0) + 1)
  }
  return Array.from(byService.entries())
    .map(([service, count]) => ({ service, count }))
    .sort((a, b) => a.service.localeCompare(b.service))
}

/** A selectable card in the access-level picker (read-only vs read+write). */
function SelectableOptionCard({
  selected,
  onSelect,
  children,
}: {
  selected: boolean
  onSelect: () => void
  children: ReactNode
}) {
  return (
    <button
      type='button'
      onClick={onSelect}
      className={`rounded-sm border px-3 py-2 text-left text-[12px] transition-colors ${
        selected
          ? 'border-ring bg-selected text-text-secondary'
          : 'border-border bg-card text-text-subtle hover:border-border-divider'
      }`}
    >
      {children}
    </button>
  )
}

function BetaBadge() {
  return (
    <span className='rounded-md bg-primary px-1.5 py-0.5 text-[9px] font-semibold tracking-wide text-primary-foreground uppercase'>
      Beta
    </span>
  )
}

function CloudAccessLevelSelector({
  accessLevel,
  onChange,
}: {
  accessLevel: CloudAccessLevel
  onChange: (accessLevel: CloudAccessLevel) => void
}) {
  return (
    <div className='space-y-2'>
      <Label className='text-[12px] font-medium text-text-secondary'>
        Access level
      </Label>
      <div className='grid grid-cols-1 gap-2 sm:grid-cols-2'>
        <SelectableOptionCard
          selected={accessLevel === 'read_only'}
          onSelect={() => onChange('read_only')}
        >
          <div className='font-medium'>Read only</div>
          <div className='mt-0.5 text-[11px] text-text-subtle'>
            Scan and collect evidence. Screenata never changes your
            infrastructure.
          </div>
        </SelectableOptionCard>
        <SelectableOptionCard
          selected={accessLevel === 'read_write'}
          onSelect={() => onChange('read_write')}
        >
          <div className='flex items-center gap-1.5 font-medium'>
            Read + write
            <BetaBadge />
          </div>
          <div className='mt-0.5 text-[11px] text-text-subtle'>
            Also lets Screenata auto-remediate findings after review.
          </div>
        </SelectableOptionCard>
      </div>
    </div>
  )
}

type SetupMethodOption<T extends string> = {
  value: T
  title: string
  description: string
  badge?: string
}

function SetupMethodSelector<T extends string>({
  label = 'Setup method',
  value,
  options,
  onChange,
}: {
  label?: string
  value: T
  options: SetupMethodOption<T>[]
  onChange: (value: T) => void
}) {
  return (
    <div className='space-y-2'>
      <Label className='text-[12px] font-medium text-text-secondary'>{label}</Label>
      <div className='grid grid-cols-1 gap-2 sm:grid-cols-2'>
        {options.map((option) => (
          <SelectableOptionCard
            key={option.value}
            selected={value === option.value}
            onSelect={() => onChange(option.value)}
          >
            <div className='flex items-center gap-1.5 font-medium'>
              {option.title}
              {option.badge ? (
                <span className='dark:bg-primary-subtle dark:text-cobalt-strong rounded-md bg-cobalt-xsubtle px-1.5 py-0.5 text-[9px] font-semibold tracking-wide text-cobalt-strong uppercase'>
                  {option.badge}
                </span>
              ) : null}
            </div>
            <div className='mt-0.5 text-[11px] text-text-subtle'>
              {option.description}
            </div>
          </SelectableOptionCard>
        ))}
      </div>
    </div>
  )
}

function DetailsPanel({
  title,
  children,
}: {
  title: string
  children: ReactNode
}) {
  return (
    <details className='rounded-sm border border-border bg-surface-subtle'>
      <summary className='cursor-pointer px-3 py-2 text-[11px] font-medium text-text-body'>
        {title}
      </summary>
      <div className='border-t border-border p-3'>{children}</div>
    </details>
  )
}

function ConnectionWizardFooter({
  embedded = false,
  showBack,
  onBack,
  secondaryLabel,
  onSecondary,
  secondaryDisabled,
  primaryLabel,
  onPrimary,
  primaryDisabled,
  primaryLoading,
  primaryIcon,
}: {
  embedded?: boolean
  showBack: boolean
  onBack?: () => void
  secondaryLabel: string
  onSecondary: () => void
  secondaryDisabled?: boolean
  primaryLabel: string
  onPrimary: () => void
  primaryDisabled?: boolean
  primaryLoading?: boolean
  primaryIcon?: ReactNode
}) {
  const content = (
    <>
      <div>
        {showBack ? (
          <Button type='button' variant='outline' onClick={onBack}>
            Back
          </Button>
        ) : null}
      </div>
      <div className='flex items-center gap-2'>
        <Button
          type='button'
          variant='outline'
          onClick={onSecondary}
          disabled={secondaryDisabled}
        >
          {secondaryLabel}
        </Button>
        <Button
          type='button'
          onClick={onPrimary}
          disabled={primaryDisabled || primaryLoading}
        >
          {primaryLoading ? (
            <>
              <Loader2 className='h-4 w-4 animate-spin' />
              {primaryLabel}
            </>
          ) : (
            <>
              {primaryIcon}
              {primaryLabel}
            </>
          )}
        </Button>
      </div>
    </>
  )

  if (embedded) {
    return (
      <div className='flex items-center justify-between border-t border-border pt-3'>
        {content}
      </div>
    )
  }

  return (
    <DialogFooter className='!flex-row items-center !justify-between p-4'>
      {content}
    </DialogFooter>
  )
}

export function toErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message) return error.message
  return fallback
}

function toIntegrationType(
  provider: CloudOAuthProvider
): 'GCP_READONLY' | 'AZURE_READONLY' {
  return provider === 'gcp' ? 'GCP_READONLY' : 'AZURE_READONLY'
}

export function isScreenataCloudProvider(
  value: string
): value is CloudProvider {
  return (
    value === 'aws' ||
    value === 'gcp' ||
    value === 'azure' ||
    value === 'cloudflare' || value === 'aliyun' || value === 'tencent'
  )
}

export function getScreenataCloudProvider(
  value: CloudProvider
): CloudProviderDefinition {
  return SCREENATA_CLOUD_PROVIDER_MAP[value]
}

function copyToClipboard(text: string) {
  navigator.clipboard.writeText(text)
  toast.success('Copied to clipboard')
}

// ---------------------------------------------------------------------------
// Azure ARM Setup Step
// ---------------------------------------------------------------------------

type AzureArmStep = 'access' | 'method' | 'authorize' | 'validate'
type AzureSetupMethod = 'portal' | 'cli' | 'powershell' | 'template'
type AwsAuthModel = 'iam_role' | 'access_keys'
type AwsRoleSetupMethod = 'cloudshell' | 'manual'

export function AzureArmSetupInline({
  connectionId,
  accessLevel: initialAccessLevel = 'read_only',
  step,
  setupMethod,
  onStepChange,
  onSetupMethodChange,
  onVerified,
  onSkip,
  onReauthorize,
  isReauthorizing = false,
}: {
  connectionId?: string | null
  accessLevel?: CloudAccessLevel
  step: AzureArmStep
  setupMethod: AzureSetupMethod
  onStepChange: (step: AzureArmStep) => void
  onSetupMethodChange: (method: AzureSetupMethod) => void
  onVerified: () => void
  onSkip: () => void
  onReauthorize?: () => void
  isReauthorizing?: boolean
}) {
  const [isVerifying, setIsVerifying] = useState(false)
  const [accessLevel, setAccessLevel] =
    useState<CloudAccessLevel>(initialAccessLevel)

  const armSetupQuery = useQuery(
    trpc.integrations.getAzureArmSetup.queryOptions({
      provider: 'azure' as const,
      ...(connectionId ? { connectionId } : {}),
      accessLevel,
    })
  )

  useEffect(() => {
    if (setupMethod === 'template') {
      onSetupMethodChange('portal')
    }
  }, [onSetupMethodChange, setupMethod])

  if (armSetupQuery.isLoading) {
    return (
      <div className='flex items-center justify-center py-6'>
        <Loader2 className='h-4 w-4 animate-spin text-text-subtle' />
      </div>
    )
  }

  if (armSetupQuery.error || !armSetupQuery.data) {
    return (
      <div className='space-y-2'>
        <p className='text-[11px] text-text-subtle'>
          {armSetupQuery.error?.message ?? 'Could not load Azure infrastructure setup details. Try again or reauthorize this connection.'}
        </p>
        {onReauthorize && (
          <Button variant='outline' size='sm' disabled={isReauthorizing} onClick={onReauthorize}>
            {isReauthorizing ? 'Authorizing…' : 'Reauthorize'}
          </Button>
        )}
        <Button
          variant='outline'
          size='sm'
          onClick={onSkip}
          className='text-[11px]'
        >
          Skip for now
        </Button>
      </div>
    )
  }

  const data = armSetupQuery.data
  const azurePortalIamUrl = `https://portal.azure.com/#@/resource/subscriptions/${encodeURIComponent(data.subscriptionId)}/users`

  const copyButton = (value: string | null | undefined, label: string) =>
    value ? (
      <Button
        type='button'
        size='sm'
        variant='outline'
        onClick={() => copyToClipboard(value)}
        className='h-7 text-[11px]'
      >
        <Copy className='h-3 w-3' />
        {label}
      </Button>
    ) : null

  const openAzurePortalIam = () => {
    window.open(azurePortalIamUrl, '_blank', 'noopener,noreferrer')
  }

  const renderCommandList = (commands: string[]) =>
    commands.map((cmd, i) => (
      <div
        key={i}
        className='group relative rounded-sm border border-border bg-[#1A1A1A] p-2'
      >
        <pre className='pr-7 font-mono text-[10px] leading-relaxed break-all whitespace-pre-wrap text-[#E0E0E0]'>
          {cmd}
        </pre>
        <button
          onClick={() => copyToClipboard(cmd)}
          className='absolute top-1.5 right-1.5 rounded-sm p-0.5 opacity-0 transition-opacity group-hover:opacity-100 hover:bg-white/10'
        >
          <Copy className='h-3 w-3 text-text-placeholder' />
        </button>
      </div>
    ))

  const setAzureAccessLevel = (nextAccessLevel: CloudAccessLevel) => {
    setAccessLevel(nextAccessLevel)
  }

  const goBack = () => {
    if (step === 'method') onStepChange('access')
    if (step === 'authorize') onStepChange('method')
    if (step === 'validate') onStepChange('authorize')
  }

  const goNext = () => {
    if (step === 'access') onStepChange('method')
    if (step === 'method') onStepChange('authorize')
    if (step === 'authorize') onStepChange('validate')
  }

  const renderAzureAuthorizeContent = () => {
    if (setupMethod === 'cli') {
      return (
        <div className='space-y-2'>
          {renderCommandList(data.azCliCommands)}
          {data.principalIdResolved ? (
            <p className='text-[10px] text-emerald-700'>
              Service principal detected. Commands are ready to run. Copy and
              paste into your terminal.
            </p>
          ) : (
            <p className='text-[10px] text-text-subtle'>
              Commands use your app&apos;s Client ID (
              <code className='rounded-sm bg-muted px-1 py-0.5 text-[9px]'>
                {data.clientId}
              </code>
              ). Azure CLI will resolve the service principal automatically.
            </p>
          )}
        </div>
      )
    }

    if (setupMethod === 'powershell') {
      return (
        <div className='space-y-2'>
          {renderCommandList(data.powershellCommands)}
          {data.principalIdResolved ? (
            <p className='text-[10px] text-emerald-700'>
              Service principal detected. Commands are ready to run. Copy and
              paste into PowerShell.
            </p>
          ) : (
            <p className='text-[10px] text-text-subtle'>
              Commands use your app&apos;s Client ID (
              <code className='rounded-sm bg-muted px-1 py-0.5 text-[9px]'>
                {data.clientId}
              </code>
              ).
            </p>
          )}
        </div>
      )
    }

    return (
      <div className='rounded-sm border border-border bg-card p-3'>
        <div className='flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between'>
          <div>
            <div className='text-[12px] font-medium text-text-secondary'>
              Assign roles in Azure Portal
            </div>
            <div className='mt-0.5 text-[11px] text-text-subtle'>
              Follow the subscription IAM steps, then verify the roles in
              Screenata.
            </div>
          </div>
          <div className='flex shrink-0 flex-wrap items-center gap-1.5'>
            {copyButton(data.subscriptionId, 'Copy subscription ID')}
            <Button
              type='button'
              size='sm'
              variant='outline'
              onClick={openAzurePortalIam}
              className='h-7 text-[11px]'
            >
              <ExternalLink className='h-3 w-3' />
              Open Access control (IAM)
            </Button>
          </div>
        </div>

        <ol className='mt-3 list-decimal space-y-3 pl-4 text-[12px] leading-5 text-text-body'>
          <li>
            Open the Azure Portal subscription IAM page. If the link opens
            elsewhere, go to <span className='font-medium'>Subscriptions</span>,
            choose subscription{' '}
            <code className='text-[11px]'>{data.subscriptionId}</code>, then
            open <span className='font-medium'>Access control (IAM)</span>.
          </li>
          <li>
            Click <span className='font-medium'>Add</span>, then choose{' '}
            <span className='font-medium'>Add role assignment</span>.
          </li>
          <li>
            On the <span className='font-medium'>Role</span> page, select the
            first required role. Repeat this flow for each role below.
            <div className='mt-1 space-y-1 rounded-sm border border-border bg-surface-subtle p-2'>
              {data.requiredRoles.map((role) => (
                <div
                  key={role.name}
                  className='flex flex-col gap-1 rounded-sm border border-border bg-card px-2 py-1.5 sm:flex-row sm:items-center sm:justify-between'
                >
                  <div className='min-w-0'>
                    <div className='text-[11px] font-medium text-text-secondary'>
                      {role.name}
                    </div>
                    <div className='text-[10px] leading-4 text-text-subtle'>
                      {role.description}
                    </div>
                  </div>
                  <Button
                    type='button'
                    size='sm'
                    variant='outline'
                    onClick={() => copyToClipboard(role.name)}
                    className='h-7 shrink-0 text-[11px]'
                  >
                    <Copy className='h-3 w-3' />
                    Copy role name
                  </Button>
                </div>
              ))}
            </div>
          </li>
          <li>
            On the <span className='font-medium'>Members</span> page, set{' '}
            <span className='font-medium'>Assign access to</span> to{' '}
            <span className='font-medium'>
              User, group, or service principal
            </span>
            {', then click '}
            <span className='font-medium'>+ Select members</span>. Search by the
            Screenata service principal display name or email-style entry, then
            use the identifiers below to confirm you selected the right app. The
            Azure picker does not search by object ID or client ID directly.
            <div className='mt-1 grid gap-1 rounded-sm border border-border bg-surface-subtle p-2'>
              <div className='rounded-sm border border-border bg-card px-2 py-1.5'>
                <div className='text-[10px] font-medium text-text-subtle'>
                  Service principal object ID for verification
                </div>
                <code className='mt-0.5 block text-[10px] break-all text-text-secondary'>
                  {data.principalId ?? 'Not resolved from Microsoft Graph'}
                </code>
                <div className='mt-1 flex justify-end'>
                  {data.principalId ? (
                    copyButton(data.principalId, 'Copy object ID')
                  ) : (
                    <span className='text-[10px] text-text-subtle'>
                      Use the client ID below to look up the app in Entra.
                    </span>
                  )}
                </div>
              </div>
              <div className='rounded-sm border border-border bg-card px-2 py-1.5'>
                <div className='text-[10px] font-medium text-text-subtle'>
                  Application / client ID for Entra lookup
                </div>
                <code className='mt-0.5 block text-[10px] break-all text-text-secondary'>
                  {data.clientId}
                </code>
                <div className='mt-1 flex justify-end'>
                  {copyButton(data.clientId, 'Copy client ID')}
                </div>
              </div>
            </div>
          </li>
          <li>
            Select the Screenata service principal, click{' '}
            <span className='font-medium'>Select</span>, then continue to{' '}
            <span className='font-medium'>Review + assign</span>. Confirm the
            scope is{' '}
            <code className='text-[11px]'>
              /subscriptions/{data.subscriptionId}
            </code>
            {', then click '}
            <span className='font-medium'>Review + assign</span>.
          </li>
          <li>
            Repeat the role assignment flow until every required role above has
            been assigned to the same Screenata service principal.
          </li>
        </ol>
      </div>
    )
  }

  const azureSetupMethodOptions: SetupMethodOption<AzureSetupMethod>[] = [
    {
      value: 'portal',
      title: 'Azure Portal',
      description: 'Recommended. Assign roles in the subscription IAM UI.',
      badge: 'Recommended',
    },
    {
      value: 'cli',
      title: 'Azure CLI',
      description: 'Copy role-assignment commands into your terminal.',
    },
    {
      value: 'powershell',
      title: 'PowerShell',
      description: 'Use Az PowerShell role-assignment commands.',
    },
  ]

  const handleVerify = async () => {
    setIsVerifying(true)
    try {
      const result = await trpcClient.integrations.verifyAzureArmRoles.mutate({
        provider: 'azure' as const,
        ...(connectionId ? { connectionId } : {}),
        accessLevel,
      })
      if (result.allAssigned) {
        toast.success('ARM roles verified. All infrastructure checks available')
        onVerified()
      } else {
        toast.error(`Missing roles: ${result.missing.join(', ')}`)
      }
    } catch (error) {
      toast.error(toErrorMessage(error, 'Failed to verify roles'))
    } finally {
      setIsVerifying(false)
    }
  }

  return (
    <div className='space-y-3'>
      {step === 'access' && (
        <div className='space-y-3'>
          <CloudAccessLevelSelector
            accessLevel={accessLevel}
            onChange={setAzureAccessLevel}
          />
          <DetailsPanel title='Why Screenata needs this access'>
            <div className='flex items-start gap-2'>
              <Shield className='mt-0.5 h-3.5 w-3.5 shrink-0 text-cobalt-strong' />
              <p className='text-[11px] leading-4 text-text-body'>
                OAuth grants access to Entra ID (users, policies, MFA).
                Infrastructure checks (Defender, storage, networking, SQL)
                require{' '}
                {accessLevel === 'read_write' ? (
                  <>
                    <strong>Contributor</strong>, <strong>Reader</strong>, and{' '}
                    <strong>Security Reader</strong>
                  </>
                ) : (
                  <>
                    <strong>Reader</strong> and <strong>Security Reader</strong>
                  </>
                )}{' '}
                assigned to the app on your Azure subscription.
              </p>
            </div>
          </DetailsPanel>
        </div>
      )}

      {step === 'method' && (
        <SetupMethodSelector
          value={setupMethod}
          onChange={onSetupMethodChange}
          options={azureSetupMethodOptions}
        />
      )}

      {step === 'authorize' && (
        <div className='space-y-3'>
          <div className='rounded-sm border border-amber-200 bg-amber-50 p-2.5'>
            <p className='text-[10px] leading-relaxed text-status-orange-strong'>
              <strong>Prerequisite:</strong> The Azure user assigning these
              roles must have <strong>Owner</strong> or{' '}
              <strong>User Access Administrator</strong> on the subscription.
              This may be a different admin than the person who completed OAuth.
            </p>
          </div>
          {renderAzureAuthorizeContent()}
        </div>
      )}

      {step === 'validate' && (
        <div className='space-y-3 rounded-sm border border-border bg-card p-3'>
          <div className='text-[12px] font-medium text-text-secondary'>
            Verify Azure roles
          </div>
          <p className='text-[11px] leading-4 text-text-subtle'>
            After assigning every required role, verify that Screenata can read
            the Azure Resource Manager APIs for this subscription.
          </p>
          <div className='space-y-1 rounded-sm border border-border bg-surface-subtle p-2'>
            {data.requiredRoles.map((role) => (
              <div
                key={role.name}
                className='flex items-center gap-1.5 text-[11px] text-text-body'
              >
                <Check className='h-3 w-3 shrink-0 text-emerald-600' />
                {role.name}
              </div>
            ))}
          </div>
        </div>
      )}

      <ConnectionWizardFooter
        embedded
        showBack={step !== 'access'}
        onBack={goBack}
        secondaryLabel='Skip for now'
        onSecondary={onSkip}
        primaryLabel={
          step === 'validate'
            ? isVerifying
              ? 'Verifying...'
              : 'Verify roles'
            : 'Next'
        }
        onPrimary={step === 'validate' ? handleVerify : goNext}
        primaryDisabled={isVerifying}
        primaryLoading={isVerifying}
        primaryIcon={step === 'validate' ? <Check className='h-4 w-4' /> : null}
      />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Cloud Connection Dialog
// ---------------------------------------------------------------------------

/**
 * RFC-197 §14 — shared connect-wizard stepper. Every cloud provider runs the
 * same pipeline frame (Connect → Configure/Scope → Validate); only the step
 * *content* differs (AWS: choose method + role; Azure: OAuth + ARM roles; …).
 * `activeIndex` is the 0-based current step; earlier steps render as done.
 */
function ConnectionWizardStepper({
  steps,
  activeIndex,
}: {
  steps: string[]
  activeIndex: number
}) {
  return (
    <div className='flex items-center gap-1 pb-1 text-[11px]'>
      {steps.map((label, i) => {
        const active = i === activeIndex
        const done = i < activeIndex
        return (
          <Fragment key={label}>
            <div
              className={`flex shrink-0 items-center gap-1.5 ${
                active
                  ? 'text-step-active'
                  : done
                    ? 'text-text-secondary'
                    : 'text-text-placeholder'
              }`}
            >
              <span
                className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[9px] leading-none font-semibold ${
                  active
                    ? 'bg-primary text-primary-foreground'
                    : done
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-border text-text-subtle'
                  }`}
              >
                <span
                  className={
                    i > 0 && (done || active) ? 'relative -top-px' : undefined
                  }
                >
                  {i + 1}
                </span>
              </span>
              <span className='font-medium'>{label}</span>
            </div>
            {i < steps.length - 1 && (
              <span
                aria-hidden='true'
                className={`h-px min-w-0 flex-1 ${
                  done ? 'bg-primary' : 'bg-border'
                }`}
              />
            )}
          </Fragment>
        )
      })}
    </div>
  )
}

export function CloudConnectionDialog({
  provider,
  open,
  isSaving,
  isOAuthConnecting,
  initialSummary,
  editingConnectionId,
  connectionLabel,
  connectionAvailable = true,
  settingsLoading = false,
  isAlreadyConnected,
  activeConnectionId,
  connectResult,
  resourceServices = [],
  resourceUsage = {},
  resourceUsageMetadata = {},
  resourceAccounts = [],
  isResourceUsageLoading = false,
  isResourceUsageSaving = false,
  onOpenChange,
  onSave,
  onConnectOAuth,
  onConnectionComplete,
  onSaveResourceUsage,
}: {
  provider: CloudProviderDefinition | null
  open: boolean
  isSaving: boolean
  isOAuthConnecting: boolean
  editingConnectionId?: string | null
  connectionLabel?: string
  connectionAvailable?: boolean
  settingsLoading?: boolean
  initialSummary: Record<string, unknown>
  /** Whether this provider already has an active connection */
  isAlreadyConnected?: boolean
  activeConnectionId?: string | null
  /** RFC-197 P2.2 — when set (post-connect), the dialog shows the Phase-D
   *  result instead of the connect form. Cleared by the consumer on close. */
  connectResult?: { provider: CloudProvider } | null
  resourceServices?: CloudResourceService[]
  resourceUsage?: CloudResourceUsage
  resourceUsageMetadata?: CloudResourceUsageMetadata
  resourceAccounts?: CloudConnectionAccountDTO[]
  isResourceUsageLoading?: boolean
  isResourceUsageSaving?: boolean
  onOpenChange: (open: boolean) => void
  onSave: (input: CloudConnectionInput) => void
  onConnectOAuth: (
    provider: CloudOAuthProvider,
    accessLevel?: CloudAccessLevel
  ) => void
  /** Called when a connection finishes via a non-save path (Azure ARM
   *  verify/skip), so the consumer can advance to Services like AWS does. */
  onConnectionComplete?: (provider: CloudProvider) => void
  onSaveResourceUsage?: (input: {
    connectionId: string
    resourceUsage: CloudResourceUsage
  }) => Promise<boolean>
}) {
  const [updatingAccess, setUpdatingAccess] = useState(false)
  const [form, setForm] = useState<CloudConfigFormState>(EMPTY_CLOUD_FORM)
  const [azurePhase, setAzurePhase] = useState<'connect' | 'arm-setup'>(
    'connect'
  )
  const [awsAuthModel, setAwsAuthModel] = useState<AwsAuthModel>('iam_role')
  const [awsRoleSetupMethod, setAwsRoleSetupMethod] =
    useState<AwsRoleSetupMethod>('cloudshell')
  const [azureArmStep, setAzureArmStep] = useState<AzureArmStep>('access')
  const [azureSetupMethod, setAzureSetupMethod] =
    useState<AzureSetupMethod>('portal')
  // RFC-197 §11 — AWS connect is a wizard: Method → Access → Setup →
  // Authorize → Configure → Validate → Services (`showResult`).
  const [awsWizardStep, setAwsWizardStep] = useState<
    'method' | 'access' | 'setup' | 'authorize' | 'configure'
  >('method')
  const [showFullAwsScript, setShowFullAwsScript] = useState(false)
  const [draftResourceUsage, setDraftResourceUsage] =
    useState<CloudResourceUsage>({})
  const [resourceConnectionId, setResourceConnectionId] =
    useState<string | null>(null)
  const [manualResourceUsageSelection, setManualResourceUsageSelection] =
    useState(false)
  const discovery = useCloudResourceUsageDetection(resourceConnectionId, open)
  const { mutateAsync: detectResourceUsage, isPending: isDetectingResourceUsage } = discovery.mutation
  const prepareAliyunSetupMutation = useMutation(trpc.integrations.prepareAliyunRoleSetup.mutationOptions())
  const prepareTencentSetupMutation = useMutation(trpc.integrations.prepareTencentRoleSetup.mutationOptions())
  const roleSetupMutation = provider?.id === 'tencent' ? prepareTencentSetupMutation : prepareAliyunSetupMutation
  const prepareAwsSetupMutation = useMutation(
    trpc.integrations.prepareAwsAssumeRoleSetup.mutationOptions()
  )
  // RFC-197 — auto-detect the regions the account uses once a valid Role ARN is
  // entered, to pre-fill the Scope step. `detectedForArnRef` fires it at most
  // once per distinct ARN.
  const detectAwsRegionsMutation = useMutation(
    trpc.integrations.detectAwsRegions.mutationOptions()
  )
  const detectedForArnRef = useRef<string | null>(null)
  const resourceUsageKeyRef = useRef<string | null>(null)
  const autoDetectionKeyRef = useRef<string | null>(null)

  // Initialize form + wizard state ONCE per open-session per provider — not on
  // every parent re-render. Consumers pass a fresh `initialSummary={}` for AWS,
  // so without this guard the effect re-runs whenever the parent re-renders
  // (e.g. the mutation/invalidation churn from clicking Verify) and snaps the
  // wizard back to step 1 ("Access").
  const initializedKeyRef = useRef<string | null>(null)
  useEffect(() => {
    if (!open) {
      initializedKeyRef.current = null
      resourceUsageKeyRef.current = null
      autoDetectionKeyRef.current = null
      return
    }
    if (editingConnectionId && (!connectionAvailable || settingsLoading)) return
    const key = `${provider?.id ?? ''}:${editingConnectionId ?? ''}:${isAlreadyConnected ? 1 : 0}`
    if (initializedKeyRef.current === key) return
    initializedKeyRef.current = key

    const initialRegions = Array.isArray(initialSummary.regions)
      ? initialSummary.regions
          .filter((region): region is string => typeof region === 'string')
          .join(', ')
      : ''
    setForm({
      ...EMPTY_CLOUD_FORM,
      delegatedRoleArn: asString(initialSummary.roleArn) || '',
      delegatedRegions: initialRegions,
      awsRegion: initialRegions || asString(initialSummary.region) || '',
      cloudflareAccountId: asString(initialSummary.accountId) || '',
    })
    setUpdatingAccess(false)
    setAwsAuthModel('iam_role')
    setAwsRoleSetupMethod('cloudshell')
    setAwsWizardStep('method')
    setAzureArmStep('access')
    setAzureSetupMethod('portal')
    setShowFullAwsScript(false)
    // If Azure is already connected, skip to ARM setup phase
    if (provider?.id === 'azure' && isAlreadyConnected) {
      setAzurePhase('arm-setup')
    } else {
      setAzurePhase('connect')
    }
  }, [initialSummary, open, provider?.id, isAlreadyConnected, editingConnectionId, connectionAvailable, settingsLoading])

  useEffect(() => {
    if (!open || resourceServices.length === 0) return
    const selectedAccount = resourceAccounts.find(
      (account) => account.connectionId === resourceConnectionId
    )
    const effectiveUsage = selectedAccount?.resourceUsage ?? resourceUsage
    const key = `${provider?.id ?? ''}:${resourceConnectionId ?? ''}:${resourceServices
      .map((service) => service.id)
      .join(',')}:${JSON.stringify(effectiveUsage)}`
    if (resourceUsageKeyRef.current === key) return
    resourceUsageKeyRef.current = key
    setDraftResourceUsage(
      createCloudResourceUsageDraft(resourceServices, effectiveUsage)
    )
  }, [
    open,
    provider?.id,
    resourceConnectionId,
    resourceServices,
    resourceUsage,
    resourceAccounts,
  ])

  useEffect(() => {
    if (!open) return
    setResourceConnectionId(
      activeConnectionId ?? resourceAccounts[0]?.connectionId ?? null
    )
  }, [activeConnectionId, open, resourceAccounts])

  useEffect(() => {
    if (
      !open ||
      !provider ||
      connectResult?.provider !== provider.id ||
      !resourceConnectionId ||
      resourceServices.length === 0 ||
      isResourceUsageLoading ||
      isDetectingResourceUsage
    ) {
      return
    }
    const account = resourceAccounts.find(
      (candidate) => candidate.connectionId === resourceConnectionId
    )
    if (
      !account ||
      account.resourceUsageDetectionState !== null ||
      resourceServices.some(
        (service) => account.resourceUsage[service.id] !== undefined
      )
    ) {
      return
    }
    const key = `${provider.id}:${resourceConnectionId}`
    if (autoDetectionKeyRef.current === key) return
    autoDetectionKeyRef.current = key

    void detectResourceUsage({ connectionId: resourceConnectionId })
      .catch((error) => {
        toast.error(
          toErrorMessage(error, 'Could not detect cloud services')
        )
      })
  }, [
    connectResult?.provider,
    detectResourceUsage,
    isDetectingResourceUsage,
    isResourceUsageLoading,
    open,
    provider?.id,
    resourceAccounts,
    resourceConnectionId,
    resourceServices,
  ])

  useEffect(() => {
    setManualResourceUsageSelection(false)
  }, [open, resourceConnectionId])

  useEffect(() => {
    if (!open || provider?.id !== 'aws' || (editingConnectionId && !updatingAccess)) return
    // Region is chosen later on the Configure step; openAwsCloudShell() builds
    // the authoritative deep-link from the live form.awsRegion. We still pass
    // the best-known region so the server-returned cloudShellUrl stays
    // consistent rather than hardcoding us-east-1.
    const regionScope = getAwsRegionScope(form.awsRegion)
    prepareAwsSetupMutation.mutate({
      provider: 'aws' as const,
      region: regionScope.ok ? regionScope.region : 'us-east-1',
      accessLevel: form.accessLevel,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.accessLevel, open, provider?.id, editingConnectionId, updatingAccess])

  // RFC-197 — once a valid Role ARN is entered on the Configure step, probe the
  // account for the regions it uses and pre-fill the Scope field (non-blocking;
  // the connect-time fallback re-detects if this hasn't finished). Only pre-fill
  // when the user hasn't already picked regions — never clobber a manual choice.
  useEffect(() => {
    if (provider?.id !== 'aws' || awsWizardStep !== 'configure') return
    if (awsAuthModel === 'access_keys') return
    const roleArn = form.awsRoleArn.trim()
    const externalId = prepareAwsSetupMutation.data?.externalId
    if (!AWS_ROLE_ARN_REGEX.test(roleArn) || !externalId) return
    if (detectedForArnRef.current === roleArn) return
    detectedForArnRef.current = roleArn
    detectAwsRegionsMutation.mutate(
      { roleArn, externalId },
      {
        onSuccess: ({ regions }) => {
          if (regions.length === 0) return
          setForm((prev) => {
            const current = prev.awsRegion
              .split(/[\s,]+/)
              .map((region) => region.trim())
              .filter(Boolean)
            // Respect a manual selection; only auto-fill an empty field.
            return current.length > 0
              ? prev
              : { ...prev, awsRegion: regions.join(', ') }
          })
        },
      }
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    awsWizardStep,
    awsAuthModel,
    form.awsRoleArn,
    prepareAwsSetupMutation.data?.externalId,
    provider?.id,
  ])

  if (!provider) return null

  const isAzureArmPhase = provider.id === 'azure' && azurePhase === 'arm-setup'
  // Show the post-validation Services step for this provider.
  const showResult = !!connectResult && connectResult.provider === provider.id
  const showSettings = !!editingConnectionId && !updatingAccess && !isAzureArmPhase && !showResult
  const selectedResourceAccount =
    resourceAccounts.find(
      (account) => account.connectionId === resourceConnectionId
    ) ?? (resourceAccounts.length === 1 ? resourceAccounts[0] : undefined)
  const effectiveResourceUsageMetadata =
    selectedResourceAccount?.resourceUsageMetadata ?? resourceUsageMetadata
  const resourceUsageDetectionState = discovery.state
  const resourceUsageDetectionRunning =
    discovery.running ||
    (resourceUsageDetectionState === null && isResourceUsageLoading)
  const resourceUsageDetectionFailed =
    !isResourceUsageLoading &&
    (resourceUsageDetectionState === 'failed' ||
      resourceUsageDetectionState === null)
  const showResourceUsageFields =
    resourceUsageDetectionState === 'completed' || manualResourceUsageSelection
  // RFC-197 §14 — provider-aware wizard steps. Cloud providers split decisions
  // from execution so each step has one job.
  const wizardSteps: string[] =
    provider.id === 'aws'
      ? awsAuthModel === 'iam_role'
        ? [
            'Method',
            'Access',
            'Setup',
            'Authorize',
            'Configure',
            'Validate',
            'Services',
          ]
        : ['Method', 'Authorize', 'Configure', 'Validate', 'Services']
      : provider.id === 'azure'
        ? ['Connect', 'Access', 'Method', 'Authorize', 'Validate', 'Services']
        : ['Connect', 'Validate', 'Services']
  const activeStepIndex = showResult
    ? wizardSteps.length - 1
    : provider.id === 'aws'
      ? awsWizardStep === 'method'
        ? 0
        : awsWizardStep === 'access'
          ? 1
          : awsWizardStep === 'setup'
            ? 2
            : awsWizardStep === 'authorize'
              ? awsAuthModel === 'iam_role'
                ? 3
                : 1
              : isSaving
                ? awsAuthModel === 'iam_role'
                  ? 5
                  : 3
                : awsAuthModel === 'iam_role'
                  ? 4
                  : 2
      : provider.id === 'azure'
        ? isAzureArmPhase
          ? azureArmStep === 'access'
            ? 1
            : azureArmStep === 'method'
              ? 2
              : azureArmStep === 'authorize'
                ? 3
                : 4
          : 0
        : provider.id === 'cloudflare' || (provider.id === 'aliyun' || provider.id === 'tencent')
          ? isSaving
            ? 1
            : 0
        : isOAuthConnecting
          ? 1
          : 0
  const awsRoleArn = form.awsRoleArn.trim()
  const isAwsRoleArnValid = AWS_ROLE_ARN_REGEX.test(awsRoleArn)
  const isAwsLegacyCredentialsValid =
    form.awsAccessKeyId.trim().length > 0 &&
    form.awsSecretAccessKey.trim().length > 0
  const shouldRequireAwsRoleArn =
    provider.id === 'aws' &&
    awsWizardStep === 'configure' &&
    awsAuthModel === 'iam_role'

  const handleSubmit = () => {
    if (provider.id === 'aws') {
      const roleArn = form.awsRoleArn.trim()
      if (!AWS_ROLE_ARN_REGEX.test(roleArn)) {
        toast.error('Enter a valid AWS IAM Role ARN')
        return
      }
      if (!prepareAwsSetupMutation.data?.externalId) {
        toast.error('AWS setup details are not loaded yet')
        return
      }
      const regionScope = getAwsRegionScope(form.awsRegion)
      if (!regionScope.ok) {
        toast.error(`Invalid AWS region: ${regionScope.invalidRegions[0]}`)
        return
      }
      onSave({
        provider: 'aws',
        authMode: 'assume_role',
        roleArn,
        externalId: prepareAwsSetupMutation.data.externalId,
        region: regionScope.region,
        ...(regionScope.regions ? { regions: regionScope.regions } : {}),
        accessLevel: form.accessLevel,
      })
      return
    }
    if ((provider.id === 'aliyun' || provider.id === 'tencent')) {
      const regions = form.delegatedRegions.split(/[\s,]+/).filter(Boolean)
      if (!form.delegatedRoleArn.trim() || !regions.length || !roleSetupMutation.data) {
        toast.error('Load the role setup, then enter the role ARN and regions.')
        return
      }
      onSave({ provider: provider.id, roleArn: form.delegatedRoleArn.trim(), regions, accessLevel: 'read_only' })
      return
    }
    if (provider.id === 'cloudflare') {
      const apiToken = form.cloudflareApiToken.trim()
      const accountId = form.cloudflareAccountId.trim()
      if (!apiToken || !accountId) {
        toast.error('Cloudflare Account API token and Account ID are required')
        return
      }
      onSave({
        provider: 'cloudflare',
        apiToken,
        accountId,
        accessLevel: 'read_only',
      })
      return
    }
    onConnectOAuth(provider.id, form.accessLevel)
  }

  const handleLegacyAwsSubmit = () => {
    if (provider.id === 'aws') {
      if (!form.awsAccessKeyId.trim() || !form.awsSecretAccessKey.trim()) {
        toast.error('AWS access key and secret are required')
        return
      }
      const regionScope = getAwsRegionScope(form.awsRegion)
      if (!regionScope.ok) {
        toast.error(`Invalid AWS region: ${regionScope.invalidRegions[0]}`)
        return
      }
      onSave({
        provider: 'aws',
        authMode: 'credentials',
        accessKeyId: form.awsAccessKeyId.trim(),
        secretAccessKey: form.awsSecretAccessKey.trim(),
        ...(form.awsSessionToken.trim()
          ? { sessionToken: form.awsSessionToken.trim() }
          : {}),
        region: regionScope.region,
        ...(regionScope.regions ? { regions: regionScope.regions } : {}),
      })
      return
    }
  }

  const copyAwsSetupScript = () => {
    const script = prepareAwsSetupMutation.data?.setupScript
    if (!script) {
      toast.error('AWS setup script is not ready')
      return
    }
    copyToClipboard(script)
  }

  const openAwsCloudShell = () => {
    if (!prepareAwsSetupMutation.data?.cloudShellUrl) {
      toast.error('AWS CloudShell link is not ready')
      return
    }
    const regionScope = getAwsRegionScope(form.awsRegion)
    const region = regionScope.ok ? regionScope.region : 'us-east-1'
    const url = `https://console.aws.amazon.com/cloudshell/home?region=${encodeURIComponent(region)}`
    window.open(url, '_blank', 'noopener,noreferrer')
  }

  const openAwsIamRoles = () => {
    window.open(
      'https://console.aws.amazon.com/iam/home#/roles',
      '_blank',
      'noopener,noreferrer'
    )
  }

  const getAwsScriptPreview = () => {
    const script = prepareAwsSetupMutation.data?.setupScript ?? ''
    if (showFullAwsScript) return script
    return script.split('\n').slice(0, 34).join('\n')
  }

  const renderAwsRegionScopeField = () => {
    const selectedRegions = form.awsRegion
      .split(/[\s,]+/)
      .map((region) => region.trim())
      .filter(Boolean)
    const isDetectingRegions = detectAwsRegionsMutation.isPending
    const detectedRegions = detectAwsRegionsMutation.data?.regions ?? []
    const wasAutoFilled =
      detectedRegions.length > 0 &&
      detectedRegions.every((region) => selectedRegions.includes(region)) &&
      selectedRegions.length === detectedRegions.length
    return (
      <div className='min-w-0 space-y-2 rounded-sm border border-border bg-card p-3'>
        <Label className='flex items-center gap-2 text-[12px] font-medium text-text-secondary'>
          Choose region scope
          {isDetectingRegions && (
            <span className='inline-flex items-center gap-1 text-[11px] font-normal text-step-active'>
              <Loader2 className='h-3 w-3 animate-spin' />
              Detecting your regions…
            </span>
          )}
        </Label>
        <MultiSelect
          options={AWS_REGIONS}
          value={selectedRegions}
          onValueChange={(regions) =>
            setForm((prev) => ({ ...prev, awsRegion: regions.join(', ') }))
          }
          placeholder={
            isDetectingRegions
              ? 'Detecting your regions…'
              : 'All enabled regions'
          }
          maxCount={4}
        />
        <p className='text-[11px] leading-4 text-text-subtle'>
          {wasAutoFilled
            ? 'Pre-filled from the regions where we found resources. Add or remove to adjust.'
            : 'Leave empty to auto-detect the regions you use, or select one or more to limit scope.'}
        </p>
      </div>
    )
  }

  const renderAwsAssumeRoleFlow = () => {
    const setup = prepareAwsSetupMutation.data
    const setupError = prepareAwsSetupMutation.error
    const roleName = setup?.roleName ?? 'ScreenataTester'
    const scriptPreview = getAwsScriptPreview()
    const trustPolicyText = setup?.trustPolicy
      ? JSON.stringify(setup.trustPolicy, null, 2)
      : ''
    const supplementalReadPolicyText = setup?.supplementalReadPolicy
      ? JSON.stringify(setup.supplementalReadPolicy, null, 2)
      : ''
    const remediationPolicyText = setup?.remediationPolicy
      ? JSON.stringify(setup.remediationPolicy, null, 2)
      : ''
    const scanPolicyArns = setup?.managedPolicyArns ?? [
      'arn:aws:iam::aws:policy/SecurityAudit',
      'arn:aws:iam::aws:policy/job-function/ViewOnlyAccess',
    ]
    const scanPolicyCommands = scanPolicyArns
      .map((policyArn) =>
        [
          'aws iam attach-role-policy \\',
          `  --role-name ${roleName} \\`,
          `  --policy-arn ${policyArn}`,
        ].join('\n')
      )
      .join('\n\n')
    const showRoleArnInvalid =
      form.awsRoleArn.trim().length > 0 &&
      !AWS_ROLE_ARN_REGEX.test(form.awsRoleArn.trim())

    return (
      <div className='min-w-0 space-y-4'>
        {awsWizardStep === 'access' && (
          <div className='space-y-3'>
            <CloudAccessLevelSelector
              accessLevel={form.accessLevel}
              onChange={(accessLevel) =>
                setForm((prev) => ({ ...prev, accessLevel }))
              }
            />
            <DetailsPanel title='Permissions Screenata will request'>
              <ul className='space-y-1'>
                {(setup?.managedPolicyArns ?? scanPolicyArns).map(
                  (policyArn) => (
                    <li
                      key={policyArn}
                      className='flex items-center gap-1.5 text-[11px] text-text-body'
                    >
                      <Check className='h-3 w-3 shrink-0 text-emerald-600' />
                      <code className='text-[10px] break-all'>
                        {policyArn.split('/').pop()}
                      </code>
                      <span className='text-text-placeholder'>· read</span>
                    </li>
                  )
                )}
                {(setup?.supplementalReadActions?.length ?? 0) > 0 ? (
                  <li className='flex items-center gap-1.5 text-[11px] text-text-body'>
                    <Check className='h-3 w-3 shrink-0 text-emerald-600' />
                    <code className='text-[10px] break-all'>
                      ScreenataNativeCheckSupplementalRead
                    </code>
                    <span className='text-text-placeholder'>
                      · {setup?.supplementalReadActions.length} read actions
                    </span>
                  </li>
                ) : null}
              </ul>
              {form.accessLevel === 'read_write' &&
              (setup?.remediationActions?.length ?? 0) > 0 ? (
                <div className='border-border mt-2 space-y-1.5 border-t pt-2'>
                  <div className='text-text-body text-[11px] font-medium'>
                    Remediation (write) · Beta
                  </div>
                  <div className='flex flex-wrap gap-1'>
                    {groupAwsRemediationActions(
                      setup?.remediationActions ?? []
                    ).map(({ service, count }) => (
                      <span
                        key={service}
                        className='rounded-sm border border-border bg-card px-1.5 py-0.5 text-[10px] text-text-body'
                      >
                        {service.toUpperCase()} · {count}
                      </span>
                    ))}
                  </div>
                  <p className='text-[10px] leading-4 text-text-subtle'>
                    Screenata can change these resources to auto-fix failing
                    checks. The setup script attaches them as an inline
                    ScreenataRemediationPolicy.
                  </p>
                </div>
              ) : null}
            </DetailsPanel>
          </div>
        )}

        {awsWizardStep === 'method' && (
          <SetupMethodSelector
            label='Authorization model'
            value={awsAuthModel}
            onChange={setAwsAuthModel}
            options={[
              {
                value: 'iam_role',
                title: 'IAM Role',
                description:
                  'Recommended. Screenata assumes one role with scoped permissions.',
                badge: 'Recommended',
              },
              {
                value: 'access_keys',
                title: 'Access keys',
                description:
                  'Legacy option. Permissions come from the provided credentials.',
              },
            ]}
          />
        )}

        {awsWizardStep === 'setup' && awsAuthModel === 'iam_role' && (
          <SetupMethodSelector
            label='IAM role setup method'
            value={awsRoleSetupMethod}
            onChange={setAwsRoleSetupMethod}
            options={[
              {
                value: 'cloudshell',
                title: 'CloudShell script',
                description:
                  'Recommended. Open CloudShell and run one generated script.',
                badge: 'Recommended',
              },
              {
                value: 'manual',
                title: 'Manual IAM setup',
                description:
                  'Follow AWS Console steps and copy the created role ARN.',
              },
            ]}
          />
        )}

        {awsWizardStep === 'authorize' && (
          <div className='space-y-4'>
            {awsAuthModel === 'iam_role' &&
              awsRoleSetupMethod === 'cloudshell' && (
                <div className='min-w-0 space-y-2 rounded-sm border border-border bg-surface-subtle p-3'>
                  <div className='flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between'>
                    <div className='min-w-0'>
                      <div className='text-[12px] font-medium text-text-secondary'>
                        Run the CloudShell setup script
                      </div>
                      <div className='mt-0.5 text-[11px] text-text-subtle'>
                        {form.accessLevel === 'read_write'
                          ? `Creates role ${roleName} with scan + remediation (write) permissions.`
                          : `Creates a scan-only role named ${roleName}.`}
                      </div>
                    </div>
                    <div className='flex shrink-0 items-center gap-1.5'>
                      <Button
                        type='button'
                        size='sm'
                        variant='outline'
                        onClick={copyAwsSetupScript}
                        disabled={
                          !setup?.setupScript ||
                          prepareAwsSetupMutation.isPending
                        }
                        className='h-7 text-[11px]'
                      >
                        <Copy className='h-3 w-3' />
                        Copy
                      </Button>
                      <Button
                        type='button'
                        size='sm'
                        variant='outline'
                        onClick={openAwsCloudShell}
                        disabled={
                          !setup?.cloudShellUrl ||
                          prepareAwsSetupMutation.isPending
                        }
                        className='h-7 text-[11px]'
                      >
                        <ExternalLink className='h-3 w-3' />
                        CloudShell
                      </Button>
                    </div>
                  </div>

                  {prepareAwsSetupMutation.isPending ? (
                    <div className='flex items-center gap-2 rounded-sm border border-border bg-card px-3 py-3 text-[12px] text-text-subtle'>
                      <Loader2 className='h-3.5 w-3.5 animate-spin' />
                      Preparing setup script...
                    </div>
                  ) : setupError ? (
                    <div className='flex items-start gap-2 rounded-sm border border-status-orange/30 bg-status-orange-bg px-3 py-2 text-[12px] text-status-orange-strong dark:border-status-orange/30 dark:bg-status-orange-bg dark:text-status-orange-strong'>
                      <AlertCircle className='mt-0.5 h-3.5 w-3.5 shrink-0' />
                      <span>
                        {toErrorMessage(
                          setupError,
                          'AWS AssumeRole setup is not configured on the server.'
                        )}
                      </span>
                    </div>
                  ) : (
                    <div className='min-w-0 overflow-hidden rounded-sm border border-[#1A1A1A] bg-[#1A1A1A]'>
                      <pre className='max-h-[260px] max-w-full min-w-0 overflow-auto p-3 text-[10px] leading-relaxed break-all whitespace-pre-wrap text-[#E0E0E0]'>
                        {scriptPreview}
                      </pre>
                      {setup?.setupScript &&
                      setup.setupScript.split('\n').length > 34 ? (
                        <button
                          type='button'
                          onClick={() =>
                            setShowFullAwsScript((value) => !value)
                          }
                          className='flex w-full items-center justify-center gap-1 border-t border-white/10 px-3 py-1.5 text-[11px] text-[#CFCFCF] hover:bg-white/5'
                        >
                          <ChevronDown
                            className={`h-3 w-3 transition-transform ${showFullAwsScript ? 'rotate-180' : ''}`}
                          />
                          {showFullAwsScript ? 'Show less' : 'Show full script'}
                        </button>
                      ) : null}
                    </div>
                  )}
                </div>
              )}

            {awsAuthModel === 'iam_role' && awsRoleSetupMethod === 'manual' && (
              <div className='min-w-0 space-y-3 rounded-sm border border-border bg-card p-3'>
                <div className='flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between'>
                  <div>
                    <div className='text-[12px] font-medium text-text-secondary'>
                      Create one IAM role manually
                    </div>
                    <div className='mt-0.5 text-[11px] text-text-subtle'>
                      Follow these AWS Console steps exactly, then paste the
                      created role ARN on the next step.
                    </div>
                  </div>
                  <Button
                    type='button'
                    size='sm'
                    variant='outline'
                    className='h-7 text-[11px]'
                    onClick={openAwsIamRoles}
                  >
                    <ExternalLink className='h-3 w-3' />
                    Open IAM Roles
                  </Button>
                </div>
                {setupError ? (
                  <div className='flex items-start gap-2 rounded-sm border border-status-orange/30 bg-status-orange-bg px-3 py-2 text-[12px] text-status-orange-strong dark:border-status-orange/30 dark:bg-status-orange-bg dark:text-status-orange-strong'>
                    <AlertCircle className='mt-0.5 h-3.5 w-3.5 shrink-0' />
                    <span>
                      {toErrorMessage(
                        setupError,
                        'AWS AssumeRole setup is not configured on the server.'
                      )}
                    </span>
                  </div>
                ) : (
                  <ol className='list-decimal space-y-3 pl-4 text-[12px] leading-5 text-text-body'>
                    <li>
                      Open the AWS Console, go to{' '}
                      <span className='font-medium'>IAM</span>, open{' '}
                      <span className='font-medium'>Roles</span>, then click{' '}
                      <span className='font-medium'>Create role</span>.
                    </li>
                    <li>
                      On{' '}
                      <span className='font-medium'>Trusted entity type</span>,
                      choose{' '}
                      <span className='font-medium'>Custom trust policy</span>.
                      Paste the generated trust policy and confirm it includes
                      the External ID.
                      {trustPolicyText ? (
                        <div className='mt-1 overflow-hidden rounded-sm border border-border bg-surface-subtle'>
                          <pre className='max-h-[180px] max-w-full min-w-0 overflow-auto p-3 text-[10px] leading-relaxed break-all whitespace-pre-wrap text-text-body'>
                            {trustPolicyText}
                          </pre>
                          <div className='flex justify-end border-t border-border px-3 py-2'>
                            <Button
                              type='button'
                              size='sm'
                              variant='outline'
                              className='h-7 text-[11px]'
                              onClick={() => copyToClipboard(trustPolicyText)}
                            >
                              <Copy className='h-3 w-3' />
                              Copy trust policy
                            </Button>
                          </div>
                        </div>
                      ) : null}
                    </li>
                    <li>
                      On <span className='font-medium'>Add permissions</span>,
                      attach the managed policies below.
                      <div className='mt-1 space-y-1 rounded-sm border border-border bg-surface-subtle px-2 py-2'>
                        {scanPolicyArns.map((policyArn) => (
                          <code
                            key={policyArn}
                            className='block text-[10px] break-all text-text-secondary'
                          >
                            {policyArn}
                          </code>
                        ))}
                        <div className='flex justify-end pt-1'>
                          <Button
                            type='button'
                            size='sm'
                            variant='outline'
                            className='h-7 text-[11px]'
                            onClick={() => copyToClipboard(scanPolicyCommands)}
                          >
                            <Copy className='h-3 w-3' />
                            Copy attach commands
                          </Button>
                        </div>
                      </div>
                    </li>
                    <li>
                      Name the role{' '}
                      <code className='text-[11px]'>{roleName}</code>, review
                      the trusted entity and permissions, then create the role.
                      <div className='mt-1 flex justify-end'>
                        <Button
                          type='button'
                          size='sm'
                          variant='outline'
                          className='h-7 text-[11px]'
                          onClick={() => copyToClipboard(roleName)}
                        >
                          <Copy className='h-3 w-3' />
                          Copy role name
                        </Button>
                      </div>
                    </li>
                    <li>
                      Open the created role, choose{' '}
                      <span className='font-medium'>Add permissions</span>, then{' '}
                      <span className='font-medium'>Create inline policy</span>.
                      Choose JSON, paste the policy below, and create it as{' '}
                      <code className='text-[11px]'>
                        ScreenataNativeCheckSupplementalRead
                      </code>
                      .
                      {supplementalReadPolicyText ? (
                        <div className='mt-1 overflow-hidden rounded-sm border border-border bg-surface-subtle'>
                          <pre className='max-h-[180px] max-w-full min-w-0 overflow-auto p-3 text-[10px] leading-relaxed break-all whitespace-pre-wrap text-text-body'>
                            {supplementalReadPolicyText}
                          </pre>
                          <div className='flex justify-end border-t border-border px-3 py-2'>
                            <Button
                              type='button'
                              size='sm'
                              variant='outline'
                              className='h-7 text-[11px]'
                              onClick={() =>
                                copyToClipboard(supplementalReadPolicyText)
                              }
                            >
                              <Copy className='h-3 w-3' />
                              Copy policy
                            </Button>
                          </div>
                        </div>
                      ) : null}
                    </li>
                    <li>
                      Open the created role details page, copy the role ARN,
                      then continue to the next step.
                    </li>
                  </ol>
                )}
                {form.accessLevel === 'read_write' && remediationPolicyText ? (
                  <DetailsPanel title='Add remediation permissions (required for read + write)'>
                    <ol className='list-decimal space-y-1 pl-4 text-[12px] leading-5 text-text-body'>
                      <li>Open the created role in AWS IAM.</li>
                      <li>
                        Open the Permissions tab and create an inline policy.
                      </li>
                      <li>
                        Choose JSON, paste the policy below, and create it as
                        ScreenataRemediationPolicy.
                      </li>
                    </ol>
                    <pre className='mt-2 max-h-[180px] max-w-full min-w-0 overflow-auto rounded-sm border border-border bg-card p-3 text-[10px] leading-relaxed break-all whitespace-pre-wrap text-text-body'>
                      {remediationPolicyText}
                    </pre>
                    <div className='mt-2 flex justify-end'>
                      <Button
                        type='button'
                        size='sm'
                        variant='outline'
                        className='h-7 text-[11px]'
                        onClick={() => copyToClipboard(remediationPolicyText)}
                      >
                        <Copy className='h-3 w-3' />
                        Copy policy
                      </Button>
                    </div>
                  </DetailsPanel>
                ) : null}
              </div>
            )}

            {awsAuthModel === 'access_keys' && (
              <div className='min-w-0 space-y-3 rounded-sm border border-border bg-card p-3'>
                <div className='text-[12px] font-medium text-text-secondary'>
                  Access keys
                </div>
                <p className='text-[11px] leading-4 text-text-subtle'>
                  Static IAM access keys are less secure than an assumed role.
                  Screenata will verify the actual permissions these credentials
                  provide.
                </p>
                <div className='space-y-2'>
                  <Label
                    htmlFor='aws-access-key-id'
                    className='flex items-center justify-between gap-3'
                  >
                    <span>Access key ID</span>
                    <span className='text-[10px] font-medium text-text-subtle'>
                      Required
                    </span>
                  </Label>
                  <Input
                    id='aws-access-key-id'
                    autoComplete='off'
                    required
                    placeholder='AKIA...'
                    value={form.awsAccessKeyId}
                    onChange={(event) =>
                      setForm((prev) => ({
                        ...prev,
                        awsAccessKeyId: event.target.value,
                      }))
                    }
                  />
                </div>
                <div className='space-y-2'>
                  <Label
                    htmlFor='aws-secret-access-key'
                    className='flex items-center justify-between gap-3'
                  >
                    <span>Secret access key</span>
                    <span className='text-[10px] font-medium text-text-subtle'>
                      Required
                    </span>
                  </Label>
                  <Input
                    id='aws-secret-access-key'
                    type='password'
                    autoComplete='new-password'
                    required
                    placeholder='AWS secret access key'
                    value={form.awsSecretAccessKey}
                    onChange={(event) =>
                      setForm((prev) => ({
                        ...prev,
                        awsSecretAccessKey: event.target.value,
                      }))
                    }
                  />
                </div>
                <div className='space-y-2'>
                  <Label htmlFor='aws-session-token'>Session token</Label>
                  <Input
                    id='aws-session-token'
                    type='password'
                    autoComplete='new-password'
                    placeholder='Optional for temporary AWS credentials'
                    value={form.awsSessionToken}
                    onChange={(event) =>
                      setForm((prev) => ({
                        ...prev,
                        awsSessionToken: event.target.value,
                      }))
                    }
                  />
                </div>
                {!isAwsLegacyCredentialsValid ? (
                  <p className='text-[11px] text-text-subtle'>
                    Enter the access key ID and secret access key before
                    continuing.
                  </p>
                ) : null}
              </div>
            )}
          </div>
        )}

        {awsWizardStep === 'configure' && (
          <div className='space-y-4'>
            {awsAuthModel === 'iam_role' && (
              <div className='min-w-0 space-y-2 rounded-sm border border-border bg-card p-3'>
                <Label
                  htmlFor='aws-role-arn'
                  className='flex items-center justify-between gap-3 text-[12px] font-medium text-text-secondary'
                >
                  <span>Paste the Role ARN</span>
                  <span className='text-[10px] font-medium text-text-subtle'>
                    Required
                  </span>
                </Label>
                <Input
                  id='aws-role-arn'
                  required
                  aria-invalid={showRoleArnInvalid}
                  placeholder={`arn:aws:iam::123456789012:role/${roleName}`}
                  value={form.awsRoleArn}
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      awsRoleArn: event.target.value,
                    }))
                  }
                />
                {showRoleArnInvalid ? (
                  <p className='text-[11px] text-status-red'>
                    {`Enter a valid IAM role ARN, for example arn:aws:iam::123456789012:role/${roleName}.`}
                  </p>
                ) : !form.awsRoleArn.trim() ? (
                  <p className='text-[11px] text-text-subtle'>
                    Paste the ARN from the AWS role summary before continuing.
                  </p>
                ) : null}
              </div>
            )}

            {renderAwsRegionScopeField()}
          </div>
        )}
      </div>
    )
  }

  const handleClose = (nextOpen: boolean) => {
    if (!nextOpen) {
      setAzurePhase('connect')
      setAzureArmStep('access')
      setAzureSetupMethod('portal')
    }
    onOpenChange(nextOpen)
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className='flex max-h-[calc(100vh-48px)] w-[calc(100vw-32px)] flex-col gap-0 overflow-hidden p-0 sm:max-w-[720px]'>
        <DialogHeader className='p-4'>
          <DialogTitle>
            {showSettings
              ? `Manage ${provider.name}`
              : showResult
                ? `${provider.name} services`
                : isAzureArmPhase
                  ? 'Azure: Infrastructure Access'
                  : `Connect ${provider.name}`}
          </DialogTitle>
          <DialogDescription>
            {showSettings
              ? 'Update this connection without removing its history or service scope.'
              : showResult
              ? `Review which optional ${provider.name} services this connection uses.`
              : isAzureArmPhase
                ? 'Assign Azure subscription roles so Screenata can scan your infrastructure.'
                : provider.id === 'aws'
                  ? 'Choose an AWS authorization model. Screenata will verify the actual scan and remediation capability.'
                  : (provider.id === 'aliyun' || provider.id === 'tencent')
                    ? 'Delegate read access with a cloud role. Screenata uses temporary credentials and scans only the regions you select.'
                  : provider.id === 'cloudflare'
                    ? 'Connect an account-owned API token with read access to account settings, zones, WAF, and R2.'
                  : provider.id === 'azure'
                    ? 'Connect with OAuth. Admin consent enables Entra ID checks immediately.'
                    : 'Use OAuth with least-privilege access for deterministic cloud scanning.'}
          </DialogDescription>
        </DialogHeader>

        <div className='min-h-0 min-w-0 flex-1 space-y-4 overflow-y-auto px-6 py-4'>
          {/* RFC-197 §14 — shared pipeline stepper (AWS / Azure) */}
          {!showSettings && (
            <ConnectionWizardStepper steps={wizardSteps} activeIndex={activeStepIndex} />
          )}
          {showSettings && (
            <div className='space-y-4'>
              <p className='text-sm font-medium'>{connectionLabel ?? 'Current connection'}</p>
              {settingsLoading ? <p className='text-muted-foreground text-sm'>Loading connection settings...</p>
                : !connectionAvailable ? <p className='text-destructive text-sm'>This connection is no longer available.</p>
                : <>
                  {provider.id === 'aws' && renderAwsRegionScopeField()}
                  <p className='text-muted-foreground text-xs'>Authorization must remain associated with this account. Use Add account to connect a different account.</p>
                  <div className='flex flex-wrap gap-3'>
                  <Button variant='outline' disabled={isSaving || isOAuthConnecting} onClick={() => {
                    if (provider.id === 'aws' || provider.id === 'cloudflare' || (provider.id === 'aliyun' || provider.id === 'tencent')) setUpdatingAccess(true)
                    else onConnectOAuth(provider.id, form.accessLevel)
                  }}>Reauthorize</Button>
                  {provider.id === 'azure' && (
                    <Button variant='outline' onClick={() => setAzurePhase('arm-setup')}>Verify infrastructure access</Button>
                  )}
                  </div>
                </>}
            </div>
          )}

          {/* Post-validation service scope. Discovery is queued by the connect
              seam, so the choices stay read-only until that job reaches a
              terminal state. */}
          {showResult && (
            <div className='space-y-4 py-2'>
              {resourceAccounts.length > 1 && (
                <div className='space-y-1.5'>
                  <Label htmlFor='cloud-resource-connection'>Cloud account</Label>
                  <select
                    id='cloud-resource-connection'
                    className='h-9 w-full rounded-sm border border-input bg-card px-3 text-sm'
                    value={resourceConnectionId ?? ''}
                    onChange={(event) =>
                      setResourceConnectionId(event.target.value || null)
                    }
                  >
                    {resourceAccounts.map((account) => (
                      <option
                        key={account.scanConnectionId}
                        value={account.connectionId ?? ''}
                      >
                        {account.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {resourceUsageDetectionRunning && (
                <div className='flex min-h-52 flex-col items-center justify-center gap-3 rounded-sm border border-border bg-surface-subtle px-6 text-center'>
                  <Loader2 className='h-6 w-6 animate-spin text-primary' />
                  <div className='space-y-1'>
                    <p className='text-sm font-medium text-text-secondary'>
                      Detecting {provider.name} services...
                    </p>
                    <p className='text-[11px] leading-4 text-text-subtle'>
                      We’re checking which services are currently used by this
                      account. This usually takes a few seconds.
                    </p>
                  </div>
                </div>
              )}
              {!resourceUsageDetectionRunning &&
                resourceUsageDetectionFailed &&
                !manualResourceUsageSelection && (
                  <div className='space-y-3 rounded-sm border border-border bg-surface-subtle px-4 py-4'>
                    <div className='flex items-start gap-2.5'>
                      <AlertCircle className='mt-0.5 h-4 w-4 shrink-0 text-status-orange' />
                      <div className='space-y-1'>
                        <p className='text-sm font-medium text-text-secondary'>
                          Service detection didn’t finish
                        </p>
                        <p className='text-[11px] leading-4 text-text-subtle'>
                          Retry automatic detection or confirm the services
                          manually.
                        </p>
                      </div>
                    </div>
                    <div className='flex gap-2 pl-6'>
                      <Button
                        type='button'
                        size='sm'
                        variant='outline'
                        onClick={async () => {
                          if (!resourceConnectionId) return
                          try {
                            await detectResourceUsage({
                              connectionId: resourceConnectionId,
                            })
                          } catch (error) {
                            toast.error(
                              toErrorMessage(
                                error,
                                'Could not detect cloud services'
                              )
                            )
                          }
                        }}
                      >
                        Retry detection
                      </Button>
                      <Button
                        type='button'
                        size='sm'
                        variant='ghost'
                        onClick={() => setManualResourceUsageSelection(true)}
                      >
                        Choose manually
                      </Button>
                    </div>
                  </div>
                )}
              {showResourceUsageFields && (
                <>
                  <p role='status' className='text-sm text-muted-foreground'>
                    {resourceServices.filter((service) => !isEssentialService(service) && draftResourceUsage[service.id] === 'USED').length} in use ·{' '}
                    {resourceServices.filter((service) => !isEssentialService(service) && draftResourceUsage[service.id] === 'NOT_USED').length} not used ·{' '}
                    {resourceServices.filter((service) => !isEssentialService(service) && draftResourceUsage[service.id] === 'UNKNOWN').length} need review.
                    {' '}Unresolved usage does not exclude related tests.
                  </p>
                  <CloudResourceUsageFields
                    key={resourceConnectionId}
                    services={resourceServices}
                    value={draftResourceUsage}
                    metadata={effectiveResourceUsageMetadata}
                    savedValue={createCloudResourceUsageDraft(resourceServices, selectedResourceAccount?.resourceUsage ?? resourceUsage)}
                    disabled={isResourceUsageSaving || resourceUsageDetectionRunning}
                    showTestCount={false}
                    onChange={setDraftResourceUsage}
                  />
                  <div className='flex w-full items-start gap-2 rounded-sm border border-border bg-surface-subtle px-3 py-2.5'>
                    <Info className='mt-px h-3.5 w-3.5 shrink-0 text-text-placeholder' />
                    <p className='text-[11px] leading-4 text-text-subtle'>
                      Services unused by every {provider.name} connection are
                      marked not applicable. Enable one later to reopen its
                      checks.
                    </p>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Phase 1: Connect */}
          {!showSettings && !showResult && !isAzureArmPhase && (
            <>
              {provider.id === 'aws' ? (
                isSaving && awsWizardStep === 'configure' ? (
                  <div className='flex min-h-52 flex-col items-center justify-center gap-3 rounded-sm border border-border bg-surface-subtle px-6 text-center'>
                    <Loader2 className='h-6 w-6 animate-spin text-primary' />
                    <div className='space-y-1'>
                      <p className='text-sm font-medium text-text-secondary'>
                        Validating AWS connection...
                      </p>
                      <p className='text-[11px] leading-4 text-text-subtle'>
                        Checking credentials and required permissions.
                      </p>
                    </div>
                  </div>
                ) : (
                  renderAwsAssumeRoleFlow()
                )
              ) : (provider.id === 'aliyun' || provider.id === 'tencent') ? (
                <div className='space-y-4'>
                  <p className='text-sm text-muted-foreground'>{provider.id === 'tencent'
                    ? 'Create a CAM role for the trusted Tencent Cloud account below. Enable External ID verification with the displayed value and attach the read policy. Then enter its ARN and the regions to collect.'
                    : 'Create a RAM role in your Alibaba Cloud account. Set the trust policy below and attach the read policy. Then enter its ARN and the regions to collect.'}</p>
                  <Button variant='outline' disabled={roleSetupMutation.isPending || isSaving}
                    onClick={() => roleSetupMutation.mutate()}>
                    {roleSetupMutation.isPending ? 'Loading setup…' : 'Load role setup'}
                  </Button>
                  {roleSetupMutation.error && <p role='alert' className='text-sm text-destructive'>{roleSetupMutation.error.message}</p>}
                  {roleSetupMutation.data && <>
                    {'trustedAccountId' in roleSetupMutation.data && <>
                      <div className='space-y-2'><Label htmlFor='tencent-trusted-account'>Trusted account ID</Label>
                        <Input id='tencent-trusted-account' readOnly value={roleSetupMutation.data.trustedAccountId} /></div>
                      <div className='space-y-2'><Label htmlFor='tencent-external-id'>External ID — enable verification in CAM</Label>
                        <Input id='tencent-external-id' readOnly value={roleSetupMutation.data.externalId} /></div>
                    </>}
                    {'trustPolicy' in roleSetupMutation.data && <div className='space-y-2'><Label htmlFor='role-trust-policy'>Role trust policy</Label>
                      <Textarea id='role-trust-policy' readOnly rows={7} value={JSON.stringify(roleSetupMutation.data.trustPolicy, null, 2)} /></div>}
                    <div className='space-y-2'><Label htmlFor='role-read-policy'>Read access policy</Label>
                      <Textarea id='role-read-policy' readOnly rows={7} value={JSON.stringify(roleSetupMutation.data.readPolicy, null, 2)} /></div>
                  </>}
                  <div className='space-y-2'><Label htmlFor='delegated-role-arn'>{provider.id === 'tencent' ? 'CAM role ARN' : 'RAM role ARN'}</Label>
                    <Input id='delegated-role-arn' autoComplete='off' placeholder={provider.id === 'tencent' ? 'qcs::cam::uin/100012345678:roleName/screenata' : 'acs:ram::1234567890123456:role/screenata'}
                      value={form.delegatedRoleArn} onChange={(event) => setForm((current) => ({ ...current, delegatedRoleArn: event.target.value }))} /></div>
                  <div className='space-y-2'><Label htmlFor='delegated-regions'>Regions</Label>
                    <Input id='delegated-regions' autoComplete='off' placeholder={provider.id === 'tencent' ? 'ap-guangzhou, ap-shanghai' : 'cn-hangzhou, ap-southeast-1'}
                      value={form.delegatedRegions} onChange={(event) => setForm((current) => ({ ...current, delegatedRegions: event.target.value }))} /></div>
                  <p className='text-xs text-muted-foreground'>{provider.id === 'tencent'
                    ? 'CAM access is account-wide. CVM and CloudAudit collection uses these regions. COS, databases, KMS and security findings are not included.'
                    : 'RAM access is account-wide. ECS and ActionTrail collection is limited to these regions. This integration does not yet scan OSS, RDS, KMS, or Security Center.'}</p>
                </div>
              ) : provider.id === 'cloudflare' ? (
                isSaving ? (
                  <div className='flex min-h-52 flex-col items-center justify-center gap-3 rounded-sm border border-border bg-surface-subtle px-6 text-center'>
                    <Loader2 className='h-6 w-6 animate-spin text-primary' />
                    <div className='space-y-1'>
                      <p className='text-sm font-medium text-text-secondary'>
                        Validating Cloudflare connection...
                      </p>
                      <p className='text-[11px] leading-4 text-text-subtle'>
                        Checking the account token and required permissions.
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className='space-y-4 rounded-sm border border-border bg-surface-subtle p-4'>
                    <div className='space-y-2'>
                      <Label htmlFor='cloudflare-api-token'>
                        Account API token
                      </Label>
                      <Input
                        id='cloudflare-api-token'
                        type='password'
                        autoComplete='new-password'
                        placeholder='Cloudflare Account API token'
                        value={form.cloudflareApiToken}
                        onChange={(event) =>
                          setForm((current) => ({
                            ...current,
                            cloudflareApiToken: event.target.value,
                          }))
                        }
                      />
                    </div>
                    <div className='space-y-2'>
                      <Label htmlFor='cloudflare-account-id'>Account ID</Label>
                      <Input
                        id='cloudflare-account-id'
                        autoComplete='off'
                        placeholder='Cloudflare account ID'
                        value={form.cloudflareAccountId}
                        onChange={(event) =>
                          setForm((current) => ({
                            ...current,
                            cloudflareAccountId: event.target.value,
                          }))
                        }
                      />
                    </div>
                    <p className='text-[11px] leading-4 text-text-subtle'>
                      Use an account-owned token with read permissions for
                      Account settings, Members, Audit Logs, Account API
                      Tokens, Zones, DNS, WAF, and Workers R2 Storage.
                    </p>
                  </div>
                )
              ) : isOAuthConnecting ? (
                <div className='flex min-h-52 flex-col items-center justify-center gap-3 rounded-sm border border-border bg-surface-subtle px-6 text-center'>
                  <Loader2 className='h-6 w-6 animate-spin text-primary' />
                  <div className='space-y-1'>
                    <p className='text-sm font-medium text-text-secondary'>
                      {provider.id === 'azure'
                        ? 'Connecting Azure...'
                        : `Validating ${provider.name} connection...`}
                    </p>
                    <p className='text-[11px] leading-4 text-text-subtle'>
                      Waiting for authorization and checking access.
                    </p>
                  </div>
                </div>
              ) : (
                <div className='text-muted-foreground text-sm'>
                  <p>
                    {provider.id === 'azure'
                      ? 'We will request Entra ID and resource management access. Admin consent is required for identity checks.'
                      : 'We will request read-only permissions and automatically detect the cloud account to scan.'}
                  </p>
                </div>
              )}
            </>
          )}

          {/* Phase 2: Azure ARM Setup */}
          {!showResult && isAzureArmPhase && (
            <AzureArmSetupInline
              connectionId={editingConnectionId ?? activeConnectionId}
              accessLevel={form.accessLevel}
              step={azureArmStep}
              setupMethod={azureSetupMethod}
              onStepChange={setAzureArmStep}
              onSetupMethodChange={setAzureSetupMethod}
              onReauthorize={() => onConnectOAuth('azure', form.accessLevel)}
              isReauthorizing={isOAuthConnecting}
              onVerified={() =>
                onConnectionComplete
                  ? onConnectionComplete(provider.id)
                  : handleClose(false)
              }
              onSkip={() =>
                onConnectionComplete
                  ? onConnectionComplete(provider.id)
                  : handleClose(false)
              }
            />
          )}
        </div>

        {showSettings && (
          <DialogFooter>
            <Button variant='ghost' onClick={() => handleClose(false)}>Close</Button>
            {provider.id === 'aws' && (
              <Button disabled={isSaving || settingsLoading || !connectionAvailable} onClick={() => {
                const scope = getAwsRegionScope(form.awsRegion)
                if (!scope.ok) return
                onSave({ provider: 'aws', region: scope.region, regions: scope.regions ?? [] })
              }}>{isSaving ? 'Saving...' : 'Save and verify'}</Button>
            )}
          </DialogFooter>
        )}
        {!showSettings && (showResult || !isAzureArmPhase) && (
          <>
            {showResult ? (
              <DialogFooter className='px-6 py-4'>
                <Button variant='outline' onClick={() => handleClose(false)}>
                  Close
                </Button>
                <Button
                  disabled={
                    !resourceConnectionId ||
                    resourceServices.length === 0 ||
                    !showResourceUsageFields ||
                    resourceUsageDetectionRunning ||
                    isResourceUsageSaving
                  }
                  onClick={async () => {
                    if (!resourceConnectionId || !onSaveResourceUsage) return
                    const saved = await onSaveResourceUsage({
                      connectionId: resourceConnectionId,
                      resourceUsage: draftResourceUsage,
                    })
                    if (saved) handleClose(false)
                  }}
                >
                  {resourceUsageDetectionRunning ? (
                    <>
                      <Loader2 className='h-4 w-4 animate-spin' />
                      Detecting services...
                    </>
                  ) : isResourceUsageSaving ? (
                    'Saving...'
                  ) : (
                    'Save and finish'
                  )}
                </Button>
              </DialogFooter>
            ) : provider.id === 'aws' ? (
              <ConnectionWizardFooter
                showBack={awsWizardStep !== 'method'}
                onBack={() => {
                  if (awsWizardStep === 'access') setAwsWizardStep('method')
                  if (awsWizardStep === 'setup') setAwsWizardStep('access')
                  if (awsWizardStep === 'authorize') {
                    setAwsWizardStep(
                      awsAuthModel === 'iam_role' ? 'setup' : 'method'
                    )
                  }
                  if (awsWizardStep === 'configure')
                    setAwsWizardStep('authorize')
                }}
                secondaryLabel='Cancel'
                onSecondary={() => handleClose(false)}
                secondaryDisabled={isSaving || isOAuthConnecting}
                primaryLabel={
                  awsWizardStep === 'configure'
                    ? isSaving
                      ? 'Verifying...'
                      : awsAuthModel === 'iam_role' &&
                          detectAwsRegionsMutation.isPending
                        ? 'Detecting regions...'
                        : 'Verify'
                    : 'Next'
                }
                onPrimary={() => {
                  if (awsWizardStep === 'method') {
                    setAwsWizardStep(
                      awsAuthModel === 'iam_role' ? 'access' : 'authorize'
                    )
                    return
                  }
                  if (awsWizardStep === 'access') {
                    setAwsWizardStep('setup')
                    return
                  }
                  if (awsWizardStep === 'setup') {
                    setAwsWizardStep('authorize')
                    return
                  }
                  if (awsWizardStep === 'authorize') {
                    setAwsWizardStep('configure')
                    return
                  }
                  if (awsAuthModel === 'access_keys') {
                    handleLegacyAwsSubmit()
                    return
                  }
                  handleSubmit()
                }}
                primaryDisabled={
                  (awsWizardStep === 'authorize' &&
                    ((awsAuthModel === 'iam_role' &&
                      (prepareAwsSetupMutation.isPending ||
                        !!prepareAwsSetupMutation.error)) ||
                      (awsAuthModel === 'access_keys' &&
                        !isAwsLegacyCredentialsValid))) ||
                  (awsWizardStep === 'configure' &&
                    (isSaving ||
                      (awsAuthModel === 'iam_role' &&
                        (prepareAwsSetupMutation.isPending ||
                          !!prepareAwsSetupMutation.error ||
                          detectAwsRegionsMutation.isPending)) ||
                      (shouldRequireAwsRoleArn && !isAwsRoleArnValid) ||
                      (awsAuthModel === 'access_keys' &&
                        !isAwsLegacyCredentialsValid)))
                }
                primaryLoading={
                  awsWizardStep === 'configure' &&
                  (isSaving ||
                    (awsAuthModel === 'iam_role' &&
                      detectAwsRegionsMutation.isPending))
                }
                primaryIcon={
                  awsWizardStep === 'configure' ? (
                    <Check className='h-4 w-4' />
                  ) : null
                }
              />
            ) : (
              <DialogFooter className='p-4'>
                <Button
                  type='button'
                  onClick={handleSubmit}
                  disabled={
                    isOAuthConnecting ||
                    isSaving ||
                    ((provider.id === 'aliyun' || provider.id === 'tencent') && (!roleSetupMutation.data || !form.delegatedRoleArn.trim() || !form.delegatedRegions.trim())) ||
                    (provider.id === 'cloudflare' &&
                      (!form.cloudflareApiToken.trim() ||
                        !form.cloudflareAccountId.trim()))
                  }
                >
                  {isOAuthConnecting || isSaving ? (
                    <>
                      <Loader2 className='h-4 w-4 animate-spin' />
                      Validating...
                    </>
                  ) : (provider.id === 'aliyun' || provider.id === 'tencent') ? (
                    provider.id === 'tencent' ? 'Connect Tencent Cloud' : 'Connect Alibaba Cloud'
                  ) : provider.id === 'cloudflare' ? (
                    'Connect Cloudflare'
                  ) : (
                    `Connect ${provider.name} with OAuth`
                  )}
                </Button>
              </DialogFooter>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}

export type GcpSccSettingsState = {
  orgId?: string | null
  sccStatus?: string | null
  sccLastTestError?: string | null
}

export function GcpSccSettingsDialog({
  open,
  isSaving,
  googleCloudConnected,
  initialSettings,
  onOpenChange,
  onSave,
  onConnectGoogleCloud,
}: {
  open: boolean
  isSaving: boolean
  googleCloudConnected: boolean
  initialSettings: GcpSccSettingsState
  onOpenChange: (open: boolean) => void
  onSave: (orgId: string) => void
  onConnectGoogleCloud: () => void
}) {
  const [orgId, setOrgId] = useState('')

  useEffect(() => {
    if (!open) return
    setOrgId(initialSettings.orgId ?? '')
  }, [initialSettings.orgId, open])

  const sccStatus = initialSettings.sccStatus ?? null
  const sccLastTestError = initialSettings.sccLastTestError ?? null

  const handleSave = () => {
    const trimmed = orgId.trim()
    if (!trimmed) {
      toast.error('Google Cloud Organization ID is required')
      return
    }
    onSave(trimmed)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='p-0 sm:max-w-[560px]'>
        <DialogHeader className='p-4'>
          <DialogTitle>Google Security Command Center</DialogTitle>
          <DialogDescription>
            Configure organization-level Security Command Center findings sync.
            This reuses your Google Cloud connection for credentials.
          </DialogDescription>
        </DialogHeader>

        <div className='space-y-4 p-4'>
          {!googleCloudConnected ? (
            <div className='space-y-3 rounded-sm border border-status-orange/30 bg-status-orange-bg px-3 py-2 text-[12px] text-status-orange-strong'>
              <p>
                Connect Google Cloud before configuring Security Command Center.
              </p>
              <Button type='button' size='sm' onClick={onConnectGoogleCloud}>
                Connect Google Cloud
              </Button>
            </div>
          ) : (
            <>
              <div className='space-y-1.5'>
                <Label htmlFor='gcp-scc-org-id'>
                  Google Cloud Organization ID
                </Label>
                <Input
                  id='gcp-scc-org-id'
                  placeholder='123456789012'
                  value={orgId}
                  onChange={(event) => setOrgId(event.target.value)}
                />
                <p className='text-[11px] leading-4 text-text-subtle'>
                  A Google Cloud organization IAM admin must grant the connected
                  account Security Center Findings Viewer or Admin Viewer on
                  this organization.
                </p>
              </div>
              {sccStatus ? (
                <div
                  className={`rounded-sm border px-3 py-2 text-[12px] ${
                    sccStatus === 'pass'
                      ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                      : 'border-status-orange/30 bg-status-orange-bg text-status-orange-strong'
                  }`}
                >
                  {sccStatus === 'pass'
                    ? 'Security Command Center access verified.'
                    : `Security Command Center access needs attention${sccLastTestError ? `: ${sccLastTestError}` : '.'}`}
                </div>
              ) : null}
            </>
          )}
        </div>

        <DialogFooter className='p-4'>
          <Button
            variant='outline'
            onClick={() => onOpenChange(false)}
            disabled={isSaving}
          >
            Cancel
          </Button>
          {googleCloudConnected && (
            <Button onClick={handleSave} disabled={isSaving}>
              {isSaving ? (
                <>
                  <Loader2 className='h-4 w-4 animate-spin' />
                  Saving...
                </>
              ) : (
                'Save and verify'
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

async function invalidateCloudQueries(
  queryClient: ReturnType<typeof useQueryClient>,
  workspaceId?: string
) {
  const invalidations = [
    queryClient.invalidateQueries({
      queryKey: trpc.integrations.getCloudConnections.queryKey(),
    }),
    queryClient.invalidateQueries({
      queryKey: trpc.integrations.listProviderConnections.queryKey(),
    }),
    queryClient.invalidateQueries({
      queryKey: trpc.integrations.getConnectedProviderKeys.queryKey(),
    }),
    queryClient.invalidateQueries({
      queryKey: trpc.complianceProgram.getProgram.queryKey(),
    }),
    // RFC-197 — connect/disconnect enqueues the evidence reconcile; refetch the
    // status query so the "Syncing checks…" badge catches a job that can start
    // and finish between the page's idle (15s) polls.
    queryClient.invalidateQueries({
      queryKey: trpc.integrations.evidenceReconcileStatus.queryKey(),
    }),
    queryClient.invalidateQueries({
      queryKey: trpc.integrations.health.list.queryKey(),
    }),
  ]

  if (workspaceId) {
    invalidations.push(
      queryClient.invalidateQueries({
        queryKey: trpc.integrations.getWorkspaceIntegrations.queryKey(),
      })
    )
  }

  await Promise.all(invalidations)
}

export function useScreenataCloudConnectionManager({
  onConnected,
  onDisconnected,
  showConnectResult = false,
}: {
  onConnected?: (provider: CloudProvider) => void
  onDisconnected?: (provider: CloudProvider) => void
  /** Opt in to the post-validation Services step instead of closing. Consumers that
   *  render `CloudConnectionDialog` via `configuringProviderId` (useIntegrationConnect,
   *  TypeformWizard) opt in; surfaces with their own close (CloudProviders) and the
   *  chat inline form keep the default close-on-success behavior. */
  showConnectResult?: boolean
} = {}) {
  const queryClient = useQueryClient()
  const { workspaceId } = useParams({ strict: false }) as {
    workspaceId?: string
  }
  const [editingConnectionId, setEditingConnectionId] = useState<string | null>(null)
  const [configuringProviderId, setConfiguringProviderId] =
    useState<CloudProvider | null>(null)
  // After a successful cloud connect the dialog stays open on Services instead
  // of closing silently. Additive:
  // only the dialog consumers read this; the chat inline form (which calls
  // `saveAndTestConnection` directly and never renders the dialog) is unaffected.
  const [connectResult, setConnectResult] = useState<{
    provider: CloudProvider
  } | null>(null)
  const clearConnectResult = () => setConnectResult(null)
  const reviewResourceUsage = (provider: CloudProvider) => {
    setConfiguringProviderId(provider)
    setConnectResult({ provider })
  }
  // The single post-success decision: show Services (integrations page /
  // onboarding) or close (settings). Shared by the AWS
  // save+test path and Azure's ARM verify/skip path so every provider ends the
  // same way regardless of which mutation established the connection.
  const completeConnection = (provider: CloudProvider) => {
    // Azure's ARM verify/skip lands here after mutating armRolesConfigured
    // server-side; refetch cloud connections so the onboarding scannable gate
    // (ConnectStageCard) sees the fresh flag and can start the now-permitted
    // Azure infra scan. Harmless double-refetch for AWS — its save+test path
    // already invalidated.
    void invalidateCloudQueries(queryClient, workspaceId)
    if (showConnectResult && !editingConnectionId) {
      setConnectResult({ provider })
    } else {
      setConfiguringProviderId(null)
    }
    onConnected?.(provider)
  }
  const [connectingProviderId, setConnectingProviderId] =
    useState<CloudOAuthProvider | null>(null)
  // Track Azure post-connect phase for the dialog
  const [azureShowArmSetup, setAzureShowArmSetup] = useState(false)
  const [activeConnectionId, setActiveConnectionId] = useState<string | null>(
    null
  )
  const [pendingAccessLevel, setPendingAccessLevel] =
    useState<CloudAccessLevel>('read_only')

  const { connectionsQuery } = useCloudResourceUsageDetection()
  const providerServicesQueryOptions =
    trpc.complianceProgram.getProviderServices.queryOptions()
  const providerServicesQuery = useQuery({
    ...providerServicesQueryOptions,
    queryKey: [...providerServicesQueryOptions.queryKey, { workspaceId }],
  })

  const upsertCloudConnectionMutation = useMutation(
    trpc.integrations.upsertCloudConnection.mutationOptions()
  )
  const testCloudConnectionMutation = useMutation(
    trpc.integrations.testCloudConnection.mutationOptions()
  )
  const disconnectCloudConnectionMutation = useMutation(
    trpc.integrations.disconnectCloudConnection.mutationOptions()
  )
  const updateProviderResourceUsageMutation = useMutation(
    trpc.integrations.updateProviderResourceUsage.mutationOptions()
  )

  const connectionsByProvider = useMemo(() => {
    const map = new Map<CloudProvider, CloudConnectionDTO>()
    for (const connection of connectionsQuery.data ?? []) {
      map.set(connection.provider, connection)
    }
    return map
  }, [connectionsQuery.data])

  const connectProviderWithOAuth = async (
    provider: CloudOAuthProvider,
    accessLevel: CloudAccessLevel = 'read_only'
  ) => {
    setConnectingProviderId(provider)
    setPendingAccessLevel(accessLevel)
    setActiveConnectionId(editingConnectionId)
    setAzureShowArmSetup(false)

    const integrationType = toIntegrationType(provider)
    const clearConnecting = () => setConnectingProviderId(current => current === provider ? null : current)

    try {
      const baseWebUrl = import.meta.env.VITE_WEB_URL ?? window.location.origin
      const callbackUrl = `${baseWebUrl}/auth-callback/${integrationType}`
      const initResult = await trpcClient.integrations.initCloudAuth.mutate({
        provider,
        redirectUrl: callbackUrl,
        ...(editingConnectionId ? { connectionId: editingConnectionId } : {}),
      })

      if (!initResult.authUrl) {
        throw new Error('Missing OAuth authorization URL')
      }

      openOAuthConnectPopup({
        authUrl: initResult.authUrl, windowName: `${provider}-readonly-auth`, provider: integrationType,
        onSuccess: async result => {
          await invalidateCloudQueries(queryClient, workspaceId)
          const resultConnectionId = result && typeof result === 'object' && 'connectionId' in result && typeof result.connectionId === 'string' ? result.connectionId : null
          setActiveConnectionId(resultConnectionId)
          if (provider === 'azure') {
            toast.success('Azure OAuth connected. Now set up infrastructure access')
            setAzureShowArmSetup(true)
            return
          }
          completeConnection(provider)
          toast.success(`${getScreenataCloudProvider(provider).name} connected`)
        },
        onError: message => toast.error(message),
        onPopupBlocked: () => toast.error('OAuth popup was blocked. Allow popups and try again.'),
        onFinish: clearConnecting,
      })
    } catch (error: unknown) {
      clearConnecting()
      toast.error(
        toErrorMessage(error, `Failed to connect ${provider.toUpperCase()}`)
      )
    }
  }

  const saveAndTestConnection = async (input: CloudConnectionInput) => {
    try {
      const upsertResult =
        await upsertCloudConnectionMutation.mutateAsync({
          ...input,
          ...(editingConnectionId ? { connectionId: editingConnectionId } : {}),
        })
      setActiveConnectionId(upsertResult.connectionId ?? null)

      await invalidateCloudQueries(queryClient, workspaceId)

      const testResult = await testCloudConnectionMutation.mutateAsync({
        provider: input.provider,
        ...(upsertResult.connectionId
          ? { connectionId: upsertResult.connectionId }
          : {}),
      })

      if (!testResult.success) {
        setConfiguringProviderId(null)
        toast.warning('Connection saved, but the follow-up test failed', {
          duration: 8000,
          description: testResult.message || undefined,
        })
        onConnected?.(input.provider)
        return true
      }

      // Keep the dialog open on Services (showConnectResult) or close on
      // success (chat inline form, CloudProviders).
      completeConnection(input.provider)

      if (upsertResult.accessLevel === 'read_write') {
        toast.success(
          `${getScreenataCloudProvider(input.provider).name} connected with read + write access`
        )
      } else {
        toast.success(
          `${getScreenataCloudProvider(input.provider).name} connected for scans`
        )
      }
      return true
    } catch (error: unknown) {
      toast.error(
        toErrorMessage(
          error,
          `Failed to connect ${getScreenataCloudProvider(input.provider).name}`
        )
      )
      return false
    }
  }

  const disconnectProvider = async (provider: CloudProvider) => {
    try {
      await disconnectCloudConnectionMutation.mutateAsync({ provider })
      await invalidateCloudQueries(queryClient, workspaceId)
      toast.success('Provider disconnected')
      onDisconnected?.(provider)
      return true
    } catch (error: unknown) {
      toast.error(toErrorMessage(error, 'Failed to disconnect provider'))
      return false
    }
  }

  const saveResourceUsage = async (input: {
    connectionId: string
    resourceUsage: CloudResourceUsage
    discoveryJobId?: string
  }) => {
    try {
      const result = await updateProviderResourceUsageMutation.mutateAsync(input)
      await Promise.all([
        invalidateCloudQueries(queryClient, workspaceId),
        queryClient.invalidateQueries({
          queryKey: trpc.checker.listProviderChecks.queryKey(),
        }),
        queryClient.invalidateQueries({
          queryKey: trpc.complianceProgram.getGuidedPath.queryKey(),
        }),
      ])
      const testChanges: string[] = []
      if (result.markedNotApplicableTests > 0) {
        testChanges.push(
          `${result.markedNotApplicableTests} test${result.markedNotApplicableTests === 1 ? '' : 's'} marked not applicable`
        )
      }
      if (result.restoredTests > 0) {
        testChanges.push(
          `${result.restoredTests} test${result.restoredTests === 1 ? '' : 's'} restored from not applicable`
        )
      }
      toast.success('Service usage saved', {
        description: testChanges.length > 0 ? `${testChanges.join('. ')}.` : undefined,
      })
      return true
    } catch (error: unknown) {
      toast.error(toErrorMessage(error, 'Failed to save resource usage'))
      return false
    }
  }

  return {
    configuringProviderId,
    setConfiguringProviderId,
    editingConnectionId,
    setEditingConnectionId,
    connectResult,
    clearConnectResult,
    reviewResourceUsage,
    completeConnection,
    connectingProviderId,
    connectionsQuery,
    providerServicesQuery,
    connectionsByProvider,
    connectProviderWithOAuth,
    disconnectProvider,
    saveAndTestConnection,
    upsertCloudConnectionMutation,
    testCloudConnectionMutation,
    disconnectCloudConnectionMutation,
    updateProviderResourceUsageMutation,
    saveResourceUsage,
    azureShowArmSetup,
    setAzureShowArmSetup,
    activeConnectionId,
    setActiveConnectionId,
    pendingAccessLevel,
  }
}

export function CloudConnectionManagerDialog({
  cloudManager,
  onClose,
}: {
  cloudManager: ReturnType<typeof useScreenataCloudConnectionManager>
  onClose?: () => void
}) {
  const configuringProvider = cloudManager.configuringProviderId
    ? getScreenataCloudProvider(cloudManager.configuringProviderId)
    : null
  const configuringConnection = cloudManager.configuringProviderId
    ? cloudManager.connectionsByProvider.get(cloudManager.configuringProviderId)
    : undefined
  const selectedConnectionId = cloudManager.editingConnectionId ?? cloudManager.activeConnectionId
  const configuringAccount = selectedConnectionId
    ? configuringConnection?.accounts.find((account) => account.connectionId === selectedConnectionId)
    : configuringConnection?.accounts[0]
  const resourceServices = configuringProvider
    ? (cloudManager.providerServicesQuery.data?.[configuringProvider.id] ?? [])
    : []

  return (
    <CloudConnectionDialog
      provider={configuringProvider}
      open={!!configuringProvider}
      editingConnectionId={cloudManager.editingConnectionId}
      connectionLabel={configuringAccount?.label}
      settingsLoading={cloudManager.connectionsQuery.isFetching}
      connectionAvailable={!!configuringAccount}
      isSaving={
        cloudManager.upsertCloudConnectionMutation.isPending ||
        cloudManager.testCloudConnectionMutation.isPending
      }
      isOAuthConnecting={!!cloudManager.connectingProviderId}
      initialSummary={
        cloudManager.editingConnectionId
          ? (configuringAccount?.configSummary ?? {})
          : configuringProvider?.id === 'aws'
            ? {}
            : (configuringConnection?.configSummary ?? {})
      }
      isAlreadyConnected={
        configuringProvider?.id === 'azure'
          ? cloudManager.azureShowArmSetup
          : false
      }
      activeConnectionId={
        cloudManager.activeConnectionId ??
        configuringAccount?.connectionId ??
        null
      }
      connectResult={cloudManager.connectResult}
      resourceServices={resourceServices}
      resourceUsage={configuringAccount?.resourceUsage ?? {}}
      resourceUsageMetadata={configuringAccount?.resourceUsageMetadata ?? {}}
      resourceAccounts={configuringConnection?.accounts ?? []}
      isResourceUsageLoading={cloudManager.connectionsQuery.isFetching}
      isResourceUsageSaving={
        cloudManager.updateProviderResourceUsageMutation.isPending
      }
      onSaveResourceUsage={cloudManager.saveResourceUsage}
      onConnectionComplete={cloudManager.completeConnection}
      onOpenChange={(open) => {
        if (!open) {
          cloudManager.setConfiguringProviderId(null)
          cloudManager.setAzureShowArmSetup(false)
          cloudManager.setActiveConnectionId(null)
          cloudManager.setEditingConnectionId(null)
          cloudManager.clearConnectResult()
          onClose?.()
        }
      }}
      onSave={(input) => {
        void cloudManager.saveAndTestConnection(input)
      }}
      onConnectOAuth={(provider, accessLevel) => {
        void cloudManager.connectProviderWithOAuth(provider, accessLevel)
      }}
    />
  )
}
